import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  sendAIQuery, estimateAIQuery, confirmAIQuery, getConnections, 
  getQueryHistory, getConversation, deleteConversation, 
  submitBackgroundQuery, getPendingQueries, cancelQuery, 
  acknowledgeQueryResult, submitAIFeedback, submitQuickFeedback, retryAIAnalysis,
  getQueryTemplates, createQueryTemplate, deleteQueryTemplate, 
  trackTemplateUsage, toggleFavoriteTemplate,
  getAnnotations, createAnnotation, deleteAnnotation,
  stagedAIQuery, autonomousAIQuery, smartAIQuery, smartAIQueryStream, saveComprehensiveLearning,
  runInvestigation, getAnalystRoles, executeConfirmedQuery, refreshAllSchemas,
  saveTermMapping
} from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import DataVisualization from '../components/DataVisualization';
import { 
  Send, 
  Sparkles, 
  User, 
  Database,
  Loader2,
  Copy,
  Check,
  Lightbulb,
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Table2,
  Code,
  Plus,
  History,
  MessageSquare,
  Trash2,
  ChevronRight,
  Clock,
  AlertTriangle,
  Play,
  BarChart3,
  Download,
  X,
  Layers,
  ClipboardCopy,
  FileSpreadsheet,
  RefreshCw,
  Zap,
  GraduationCap,
  Star,
  Bookmark,
  Mic,
  MicOff,
  FileText,
  StickyNote,
  GitCompare,
  Edit,
  Search,
  ThumbsUp,
  ThumbsDown,
  Info,
  Shield,
  Target
} from 'lucide-react';

const AIQueryPage = () => {
  const location = useLocation();
  const [query, setQuery] = useState(location.state?.query || '');
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('all');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [syncingSchema, setSyncingSchema] = useState(false); // For schema refresh button
  const [conversationId, setConversationId] = useState(() => {
    // Generate a unique conversation ID using crypto for better uniqueness
    const generateConvId = () => `conv-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    return location.state?.conversationId || generateConvId();
  });
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [expandedCharts, setExpandedCharts] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [feedbackOpen, setFeedbackOpen] = useState({});
  const [feedbackText, setFeedbackText] = useState({});
  const [feedbackSending, setFeedbackSending] = useState(false);

  // Recent Players (last 10 players the analyst has investigated)
  const [recentPlayers, setRecentPlayers] = useState([]);

  // AI provider health probe (Anthropic via Emergent LLM key)
  const [llmHealth, setLlmHealth] = useState(null);
  const [llmHealthLoading, setLlmHealthLoading] = useState(false);
  const checkLlmHealth = useCallback(async () => {
    setLlmHealthLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLlmHealth({ ok: false, error: 'Not logged in' });
        return;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/llm-health`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      setLlmHealth(data);
      if (data.ok) {
        toast.success(`AI provider OK (${data.latency_ms}ms)`);
      } else {
        toast.error(`AI provider issue: ${data.error?.substring(0, 80) || 'unknown'}`);
      }
    } catch (e) {
      setLlmHealth({ ok: false, error: e.message });
      toast.error('Health probe failed: ' + e.message);
    } finally {
      setLlmHealthLoading(false);
    }
  }, []);
  
  // Two-phase query state
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [currentEstimate, setCurrentEstimate] = useState(null);
  const [pendingQuery, setPendingQuery] = useState('');
  
  // AI thinking/progress state
  const [thinkingStatus, setThinkingStatus] = useState(null);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [aiThoughts, setAiThoughts] = useState([]);  // Rich AI thinking events streamed via SSE
  const [slowQueryHint, setSlowQueryHint] = useState(false);  // Shown after 30s of waiting
  const [abortController, setAbortController] = useState(null);
  const [activeQueryId, setActiveQueryId] = useState(null);
  
  // Staged AI Process State
  const [aiStage, setAiStage] = useState(null); // 'understanding' | 'planning' | 'executing' | 'analyzing' | 'needs_clarification'
  const [aiUnderstanding, setAiUnderstanding] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [awaitingUserInput, setAwaitingUserInput] = useState(false);
  const [userCorrectionInput, setUserCorrectionInput] = useState('');
  const [pendingClarification, setPendingClarification] = useState(null); // Stores clarification context for retry
  
  // AI Mode Selection
  const [useAutonomousMode, setUseAutonomousMode] = useState(false); // Default to Smart mode now
  const [useSmartMode, setUseSmartMode] = useState(true); // New Smart Two-Stage mode
  const [adminPreviewMode, setAdminPreviewMode] = useState(false); // Admin: Preview query before running
  const [explainAnalysis, setExplainAnalysis] = useState(null); // EXPLAIN analysis results
  const [adminTrainingFeedback, setAdminTrainingFeedback] = useState(''); // Feedback text for admin training mode
  
  // Conversation Context Management
  const CONTEXT_LIMIT = 10; // Max messages before suggesting summarization
  const [showContextWarning, setShowContextWarning] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  
  // Check if approaching context limit
  const messageCount = messages.filter(m => m.type === 'user' || (m.type === 'assistant' && m.results)).length;
  const isApproachingLimit = messageCount >= CONTEXT_LIMIT - 2;
  const isAtLimit = messageCount >= CONTEXT_LIMIT;
  
  // Admin Preview State (for pre-query review)
  const [adminPreview, setAdminPreview] = useState(null); // Stores pending query preview for admin approval
  
  // Investigation State
  const [investigations, setInvestigations] = useState({}); // Track investigations by message ID
  
  // Analyst Role State
  const [analystRoles, setAnalystRoles] = useState([]);
  const [selectedAnalystRole, setSelectedAnalystRole] = useState('all');
  const [canSwitchRoles, setCanSwitchRoles] = useState(false);
  const [availableRoles, setAvailableRoles] = useState(['all']);
  
  // AI Features state
  const [useAnomalyDetection, setUseAnomalyDetection] = useState(false);
  
  // Background query state
  const [backgroundQueries, setBackgroundQueries] = useState([]);
  const [showBackgroundPanel, setShowBackgroundPanel] = useState(false);
  
  // Query Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', category: 'general' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Voice Input state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  
  // Annotations state
  const [annotations, setAnnotations] = useState({});
  const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
  const [currentAnnotationQueryId, setCurrentAnnotationQueryId] = useState(null);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationType, setAnnotationType] = useState('note');
  
  // Query Comparison state
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [compareQuery, setCompareQuery] = useState('');
  const [comparePeriod1, setComparePeriod1] = useState('today');
  const [comparePeriod2, setComparePeriod2] = useState('yesterday');
  
  // Quick Feedback state (thumbs up/down)
  const [quickFeedback, setQuickFeedback] = useState({}); // { messageId: 'thumbs_up' | 'thumbs_down' }
  const [showQuickFeedbackReason, setShowQuickFeedbackReason] = useState(null); // messageId for reason input
  const [quickFeedbackReason, setQuickFeedbackReason] = useState('');
  
  // Retry Analysis state
  const [retryingAnalysis, setRetryingAnalysis] = useState({}); // { messageId: true/false }
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const { user } = useAuth();

  // Sound notification for completed queries
  const playCompletionSound = useCallback(() => {
    try {
      // Create a simple bell/chime sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Bell-like sound: two quick chimes
      oscillator.frequency.setValueAtTime(830, audioContext.currentTime); // First note
      oscillator.frequency.setValueAtTime(1046, audioContext.currentTime + 0.1); // Second note (higher)
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Sound not available:', e);
    }
  }, []);

  // Fetch query templates
  const fetchTemplates = useCallback(async () => {
    try {
      const data = await getQueryTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, []);

  // Fetch Analyst Roles
  useEffect(() => {
    const fetchAnalystRoles = async () => {
      try {
        const data = await getAnalystRoles();
        setAnalystRoles(data.roles || {});
        setSelectedAnalystRole(data.user_role || 'all');
        setCanSwitchRoles(data.can_switch || false);
        setAvailableRoles(data.available_roles || ['all']);
      } catch (error) {
        console.error('Failed to fetch analyst roles:', error);
      }
    };
    fetchAnalystRoles();
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setQuery(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access.');
        }
      };
    }
  }, []);

  // Toggle voice input
  const toggleVoiceInput = () => {
    if (!speechSupported) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
      toast.info('Listening... Speak your query');
    }
  };

  const fetchConnections = useCallback(async () => {
    try {
      const data = await getConnections();
      setConnections(data);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const history = await getQueryHistory(100);
      const grouped = {};
      history.forEach(q => {
        const convId = q.conversation_id || q.id;
        if (!grouped[convId]) {
          grouped[convId] = {
            id: convId,
            queries: [],
            firstQuery: q.query,
            lastUpdated: q.created_at,
            connection_name: q.connection_name
          };
        }
        grouped[convId].queries.push(q);
        if (new Date(q.created_at) > new Date(grouped[convId].lastUpdated)) {
          grouped[convId].lastUpdated = q.created_at;
        }
      });
      const sorted = Object.values(grouped).sort((a, b) => 
        new Date(b.lastUpdated) - new Date(a.lastUpdated)
      );
      setConversations(sorted);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  // Load Recent Players (per-analyst) — refreshed when chat is empty (landing state)
  const fetchRecentPlayers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/recent-players?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.success && Array.isArray(data.players)) {
        setRecentPlayers(data.players);
      }
    } catch (e) {
      // silent fail — feature is non-critical
    }
  }, []);
  useEffect(() => { fetchRecentPlayers(); }, [fetchRecentPlayers]);

  // Track acknowledged results to prevent duplicate toasts
  const acknowledgedResults = useRef(new Set());

  // Poll for background query results
  const fetchBackgroundQueries = useCallback(async () => {
    try {
      const data = await getPendingQueries();
      setBackgroundQueries(data.queries || []);
      
      // Check for completed queries - only show toast once per result
      const completed = data.pending_results || [];
      for (const result of completed) {
        // Skip if we've already shown toast for this result
        if (acknowledgedResults.current.has(result.query_id)) {
          continue;
        }
        
        // Mark as acknowledged immediately to prevent duplicate toasts
        acknowledgedResults.current.add(result.query_id);
        
        // Also acknowledge on the server
        try {
          await acknowledgeQueryResult(result.query_id);
        } catch (e) {
          console.error('Failed to acknowledge:', e);
        }
        
        toast.success(`Query completed: "${result.query_text.substring(0, 30)}..."`, {
          duration: 10000, // Show for 10 seconds
          action: {
            label: 'View',
            onClick: () => {
              // Add result to messages
              const aiMessage = {
                id: result.query_id,
                type: 'ai',
                content: result.result?.analysis || 'Query completed',
                sql: result.result?.sql,
                database: result.result?.database,
                rowCount: result.result?.row_count,
                results: result.result?.results,
                success: result.result?.success,
                error: result.result?.error,
                timestamp: result.completed_at,
                visualization: result.result?.visualization,
                isBackground: true
              };
              setMessages(prev => [...prev, aiMessage]);
            }
          }
        });
      }
      
      // Show panel if there are running queries
      if (data.running_count > 0) {
        setShowBackgroundPanel(true);
      }
    } catch (error) {
      console.error('Failed to fetch background queries:', error);
    }
  }, []);

  useEffect(() => {
    // Start polling for background queries
    fetchBackgroundQueries();
    pollIntervalRef.current = setInterval(fetchBackgroundQueries, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchBackgroundQueries]);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Slow-query hint — show a gentle banner after 30s of continuous loading.
  // Resets immediately when loading flips false or a new query starts.
  useEffect(() => {
    if (!loading) {
      setSlowQueryHint(false);
      return;
    }
    setSlowQueryHint(false);
    const timer = setTimeout(() => setSlowQueryHint(true), 30000);
    return () => clearTimeout(timer);
  }, [loading, thinkingStartTime]);

  // Load conversation if coming from history with a conversationId
  useEffect(() => {
    const loadConversationFromHistory = async () => {
      if (location.state?.conversationId && messages.length === 0) {
        try {
          const convData = await getConversation(location.state.conversationId);
          const queries = convData?.queries || convData || [];
          if (queries.length > 0) {
            const loadedMessages = queries.map(q => ([
              {
                id: `user-${q.id}`,
                type: 'user',
                content: q.query,
                timestamp: q.created_at
              },
              {
                id: q.id,
                type: 'ai',
                content: q.analysis || q.response || 'No response',
                sql: q.sql,
                database: q.database,
                rowCount: q.row_count,
                results: q.results,
                success: q.success,
                error: q.error,
                timestamp: q.created_at,
                visualization: q.visualization,
                cached: q.cached || false,
                originalQuery: q.query
              }
            ])).flat();
            setMessages(loadedMessages);
            setConversationId(location.state.conversationId);
          }
        } catch (error) {
          console.error('Failed to load conversation:', error);
        }
      }
    };
    
    loadConversationFromHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.conversationId]);

  useEffect(() => {
    if (location.state?.query && messages.length === 0 && !location.state?.conversationId) {
      handleSubmit(null, location.state.query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.query]);

  const toggleDetails = (messageId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleChart = (messageId) => {
    setExpandedCharts(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleTable = (messageId) => {
    setExpandedTables(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Copy results as tab-separated text (paste-friendly for Excel)
  const copyTableToClipboard = async (results, messageId) => {
    if (!results || results.length === 0) return;
    const headers = Object.keys(results[0]);
    const rows = results.map(row => headers.map(h => {
      const val = row[h];
      if (val === null || val === undefined) return '';
      // Escape tabs and newlines within cell values
      return String(val).replace(/\t/g, ' ').replace(/\n/g, ' ');
    }).join('\t'));
    const tableText = [headers.join('\t'), ...rows].join('\n');
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(tableText);
      } else {
        // Fallback: create a temporary textarea
        const textarea = document.createElement('textarea');
        textarea.value = tableText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedId(`table-${messageId}`);
      toast.success('Table copied! Paste into Excel or Google Sheets');
      setTimeout(() => setCopiedId(null), 2500);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Copy failed. Try the Download Excel button instead.');
    }
  };

  // Autonomous Investigation - triggered when anomaly detected
  const triggerInvestigation = async (messageId, message) => {
    if (investigations[messageId]?.status === 'running') return;
    
    setInvestigations(prev => ({
      ...prev,
      [messageId]: { status: 'running', progress: 'Starting investigation...' }
    }));
    
    try {
      const anomaly = message.anomalyInvestigation;
      const trendDirection = message.analysis?.trend?.direction || 'unknown';
      
      // Get metric name from the detected issue
      const metricMatch = anomaly.detected_issue?.match(/(deposits?|registrations?|users?|logins?|ftd|payments?)/i);
      const metricName = metricMatch ? metricMatch[1] : 'metric';
      
      setInvestigations(prev => ({
        ...prev,
        [messageId]: { status: 'running', progress: 'Running diagnostic queries...' }
      }));
      
      const result = await runInvestigation({
        original_question: message.originalQuestion,
        original_results: message.results?.slice(0, 20) || [],
        detected_anomaly: anomaly.detected_issue,
        metric_name: metricName,
        trend_direction: trendDirection
      });
      
      if (result.success) {
        setInvestigations(prev => ({
          ...prev,
          [messageId]: { 
            status: 'complete', 
            result: result
          }
        }));
        
        toast.success('Investigation complete! Root cause identified.', { duration: 5000 });
      } else {
        setInvestigations(prev => ({
          ...prev,
          [messageId]: { status: 'error', error: result.error }
        }));
        toast.error('Investigation failed: ' + result.error);
      }
    } catch (error) {
      console.error('Investigation error:', error);
      setInvestigations(prev => ({
        ...prev,
        [messageId]: { status: 'error', error: error.message }
      }));
      toast.error('Investigation failed: ' + error.message);
    }
  };

  const handleNewConversation = () => {
    // Generate a new unique conversation ID
    const newConvId = `conv-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setConversationId(newConvId);
    setMessages([]);
    setQuery('');
    toast.success('Started new conversation');
  };

  const handleLoadConversation = async (conv) => {
    setConversationId(conv.id);
    const loadedMessages = conv.queries.map(q => ([
      {
        id: `user-${q.id}`,
        type: 'user',
        content: q.query,
        timestamp: q.created_at
      },
      {
        id: q.id,
        type: 'ai',
        content: q.analysis || q.response || 'No response',
        sql: q.sql,
        database: q.database,
        rowCount: q.row_count,
        results: q.results,
        success: q.success,
        error: q.error,
        timestamp: q.created_at,
        visualization: q.visualization
      }
    ])).flat();
    setMessages(loadedMessages);
    setShowHistory(false);
  };

  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        handleNewConversation();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  // Two-Phase Query: Step 1 - Get estimate
  const handleEstimate = async (e, prefilledQuery = null) => {
    e?.preventDefault();
    const queryText = prefilledQuery || query.trim();
    if (!queryText) return;

    // Immediately add user message to chat and clear input
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuery(''); // Clear input immediately
    
    setEstimating(true);
    setPendingQuery(queryText);

    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const estimate = await estimateAIQuery(queryText, connectionId);
      setCurrentEstimate(estimate);
      setShowEstimateDialog(true);
    } catch (error) {
      console.error('Estimate failed:', error);
      toast.error('Failed to estimate query. Running directly...');
      // Fallback to direct execution (message already added)
      handleDirectSubmitNoMessage(queryText);
    } finally {
      setEstimating(false);
    }
  };

  // Cancel current running query (user initiated stop)
  const handleStopQuery = async () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    
    // Kill MySQL queries on the database server
    if (activeQueryId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/cancel-stream/${activeQueryId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) {
        console.warn('Failed to cancel DB query:', e);
      }
      setActiveQueryId(null);
    }
    
    setLoading(false);
    setThinkingStatus(null);
    setThinkingStartTime(null);
    setThinkingSteps([]);
    toast.info('Query cancelled and database queries killed.');
    
    // Add a message to chat
    const cancelMessage = {
      id: Date.now(),
      type: 'ai',
      content: '⏹️ **Query cancelled by user.**\n\nThe running database queries have been killed. Please explain what you actually need and I\'ll try a different approach.',
      timestamp: new Date().toISOString(),
      isCancelled: true
    };
    setMessages(prev => [...prev, cancelMessage]);
  };

  // ========== STAGED AI QUERY PROCESS ==========
  
  // Start staged query - Stage 1: Understanding
  const handleStagedQuery = async (queryText) => {
    if (!queryText?.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    
    setLoading(true);
    setThinkingStartTime(Date.now());
    setAiStage('understanding');
    setThinkingSteps([{ text: 'Reading your question...', icon: '📖' }]);
    
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const result = await stagedAIQuery(queryText, 'understand', { 
        connectionId, 
        conversationId 
      });
      
      if (result.success) {
        setAiUnderstanding(result);
        setThinkingSteps(prev => [...prev, { text: 'Understanding complete', icon: '✓' }]);
        
        // Show understanding to user
        const understandingMessage = {
          id: Date.now() + 1,
          type: 'ai',
          isStaged: true,
          stage: 'understanding',
          content: result.understanding,
          data: result,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, understandingMessage]);
        
        // If AI has questions, wait for user
        if (result.needs_input) {
          setAwaitingUserInput(true);
          setLoading(false);
        } else {
          // Auto-proceed to planning
          handleStagedPlan(queryText, result.understanding);
        }
      } else {
        throw new Error(result.error || 'Failed to understand query');
      }
    } catch (error) {
      console.error('Staged query error:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to process query';
      toast.error(errorMsg);
      
      // Show error in chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `❌ **Error:** ${errorMsg}\n\nPlease try rephrasing your question.`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      setLoading(false);
      setAiStage(null);
      setThinkingSteps([]);
    }
  };
  
  // Stage 2: Planning
  const handleStagedPlan = async (originalQuery, approvedUnderstanding, userCorrection = null) => {
    setLoading(true);
    setAiStage('planning');
    setThinkingSteps(prev => [...prev, { text: 'Planning query approach...', icon: '🗺️' }]);
    
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const result = await stagedAIQuery(originalQuery, 'plan', {
        connectionId,
        conversationId,
        approvedUnderstanding,
        userCorrection
      });
      
      if (result.success) {
        setAiPlan(result);
        setThinkingSteps(prev => [...prev, { text: 'Plan ready', icon: '✓' }]);
        
        // Show plan to user
        const planMessage = {
          id: Date.now() + 2,
          type: 'ai',
          isStaged: true,
          stage: 'planning',
          content: result.plan_summary,
          data: result,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, planMessage]);
        
        // If AI has alternatives, wait for user choice
        if (result.needs_input) {
          setAwaitingUserInput(true);
          setLoading(false);
        } else {
          // Auto-proceed to execution
          handleStagedExecute(originalQuery, result);
        }
      } else {
        throw new Error(result.error || 'Failed to plan query');
      }
    } catch (error) {
      console.error('Staged plan error:', error);
      toast.error('Failed to plan query');
      setLoading(false);
      setAiStage(null);
    }
  };
  
  // Stage 3: Execute
  const handleStagedExecute = async (originalQuery, approvedPlan, userChoice = null) => {
    setLoading(true);
    setAiStage('executing');
    setThinkingSteps(prev => [
      ...prev, 
      { text: 'Building SQL query...', icon: '🛠️' },
      { text: 'Executing on database...', icon: '⚡' }
    ]);
    
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const result = await stagedAIQuery(originalQuery, 'execute', {
        connectionId,
        conversationId,
        approvedPlan,
        userCorrection: userChoice
      });
      
      setThinkingSteps(prev => [...prev, { text: 'Analyzing results...', icon: '🧠' }]);
      
      if (result.success) {
        // Check if AI needs clarification
        if (result.stage === 'needs_clarification') {
          // AI is asking for help - show the question
          const clarificationMessage = {
            id: Date.now() + 3,
            type: 'ai',
            isStaged: true,
            stage: 'needs_clarification',
            content: result.blocking_issue,
            question: result.question,
            questionType: result.question_type,
            context: result.context || {},
            likelyCause: result.likely_cause,
            suggestedFix: result.suggested_fix,
            failedSql: result.failed_sql,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, clarificationMessage]);
          
          // Store context for retry
          setPendingClarification({
            question: result.question,
            questionType: result.question_type,
            context: result.context || {},
            failedSql: result.failed_sql
          });
          
          setAiStage('needs_clarification');
          setAwaitingUserInput(true);
          setLoading(false);
          setThinkingSteps([]);
          return; // Don't reset state yet - wait for user input
        }
        
        // Normal success - show results
        const resultMessage = {
          id: Date.now() + 3,
          type: 'ai',
          isStaged: true,
          stage: 'complete',
          content: result.analysis,
          sql: result.sql,
          database: result.database,
          results: result.results,
          rowCount: result.row_count,
          learned: result.learned, // Show if we learned something
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, resultMessage]);
        
        if (result.learned) {
          toast.success(`Query complete! ${result.row_count} rows. ${result.learned}`);
        } else {
          toast.success(`Query complete! ${result.row_count} rows returned`);
        }
      } else {
        throw new Error(result.error || 'Query execution failed');
      }
    } catch (error) {
      console.error('Staged execute error:', error);
      const errorMessage = {
        id: Date.now() + 4,
        type: 'ai',
        content: `❌ **Execution Error:** ${error.message}\n\nPlease try rephrasing your question or provide more details.`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      // Only reset if not waiting for clarification
      if (aiStage !== 'needs_clarification') {
        setLoading(false);
        setAiStage(null);
        setAiUnderstanding(null);
        setAiPlan(null);
        setAwaitingUserInput(false);
        setThinkingSteps([]);
        setPendingClarification(null);
      }
    }
  };
  
  // User provides correction/clarification during staged process
  const handleUserCorrection = async (correction) => {
    if (!correction?.trim()) return;
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: correction,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setUserCorrectionInput('');
    setAwaitingUserInput(false);
    
    // Determine which stage to continue from
    if (aiStage === 'understanding' && aiUnderstanding) {
      // User corrected understanding, proceed to planning with correction
      handleStagedPlan(pendingQuery || query, aiUnderstanding.understanding, correction);
    } else if (aiStage === 'planning' && aiPlan) {
      // User made a choice, proceed to execution
      handleStagedExecute(pendingQuery || query, aiPlan, correction);
    } else if (aiStage === 'needs_clarification' && pendingClarification) {
      // User answered a clarification question - retry with learning
      handleClarificationAnswer(correction);
    }
  };
  
  // Handle user's answer to AI's clarification question
  const handleClarificationAnswer = async (answer) => {
    setLoading(true);
    setAiStage('executing');
    setThinkingSteps([
      { text: 'Saving your knowledge...', icon: '💾' },
      { text: 'Retrying with new understanding...', icon: '🔄' }
    ]);
    
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const result = await stagedAIQuery(pendingQuery || query, 'retry_with_learning', {
        connectionId,
        conversationId,
        approvedPlan: aiPlan,
        clarificationAnswer: answer,
        clarificationContext: {
          question: pendingClarification?.question,
          questionType: pendingClarification?.questionType,
          table: pendingClarification?.context?.table,
          column: pendingClarification?.context?.column
        }
      });
      
      if (result.success) {
        if (result.stage === 'needs_clarification') {
          // Still needs more clarification
          const clarificationMessage = {
            id: Date.now() + 5,
            type: 'ai',
            isStaged: true,
            stage: 'needs_clarification',
            content: result.blocking_issue,
            question: result.question,
            questionType: result.question_type,
            context: result.context || {},
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, clarificationMessage]);
          
          setPendingClarification({
            question: result.question,
            questionType: result.question_type,
            context: result.context || {}
          });
          
          setAiStage('needs_clarification');
          setAwaitingUserInput(true);
          setLoading(false);
          setThinkingSteps([]);
          toast.info("Saved! But I need a bit more information...");
        } else if (result.stage === 'execute') {
          // Success!
          const resultMessage = {
            id: Date.now() + 5,
            type: 'ai',
            isStaged: true,
            stage: 'complete',
            content: result.analysis,
            sql: result.sql,
            database: result.database,
            results: result.results,
            rowCount: result.row_count,
            learned: result.learned,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, resultMessage]);
          toast.success(`Got it! ${result.row_count} rows returned. Knowledge saved for next time!`);
          
          // Reset state
          setLoading(false);
          setAiStage(null);
          setAiUnderstanding(null);
          setAiPlan(null);
          setAwaitingUserInput(false);
          setThinkingSteps([]);
          setPendingClarification(null);
        }
      } else {
        throw new Error(result.error || 'Retry failed');
      }
    } catch (error) {
      console.error('Clarification retry error:', error);
      toast.error('Failed to retry query');
      setLoading(false);
      setAiStage(null);
      setPendingClarification(null);
      setThinkingSteps([]);
    }
  };
  
  // User approves current stage
  const handleApproveStage = () => {
    setAwaitingUserInput(false);
    
    if (aiStage === 'understanding' && aiUnderstanding) {
      handleStagedPlan(pendingQuery || query, aiUnderstanding.understanding);
    } else if (aiStage === 'planning' && aiPlan) {
      handleStagedExecute(pendingQuery || query, aiPlan);
    }
  };
  
  // Handle clickable option selection
  const handleOptionClick = (option, context) => {
    // Add user message showing their choice
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: option,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    // If in staged process, handle as correction/choice
    if (awaitingUserInput) {
      handleUserCorrection(option);
    } else {
      // Treat as a new query response
      handleStagedQuery(option);
      setPendingQuery(option);
    }
  };
  
  // Detect if user is teaching/correcting the AI
  const isUserTeaching = (text) => {
    const t = text.trim();
    // Don't classify long natural-language queries as teaching
    if (t.length > 400) return false;
    // Skip if it looks like a new question (starts with WH-word or has "?" near the end)
    if (/^(what|when|where|who|why|how|which|show|give|list|count|sum|find|tell)\b/i.test(t)) return false;
    const teachingPatterns = [
      /^no[,.\s]/i,
      /^actually[,.]?\s/i,
      /^the\s+(table|column|database)\s+(is|should be)/i,
      /^use\s+(the\s+)?table/i,
      /^you\s+should\s+(use|do|filter|join|select|consider)/i,
      /that'?s?\s+(wrong|incorrect|not\s+right|completely\s+wrong)/i,
      /^not\s+that/i,
      /^instead[,.]?\s+/i,
      /\bmeans?\s+(that|the|to)\b/i,
      /\brefers?\s+to\b/i,
      /^for\s+this[,.]?\s+use/i,
      /^the\s+correct\s+(one|table|column|approach|sql|query)/i,
      /^it'?s?\s+(actually|not)/i,
      /^(this|that|the)\s+(report|data|result|number|output|answer)\s+is\s+(wrong|incorrect|completely\s+wrong)/i,
      /\bdata\s+is\s+(wrong|incorrect)\b/i,
      /\b(fix|correct)\s+(the|this|that)\s+(issue|bug|query|sql|output)/i,
      /\b(why|why\s+is|why\s+are)\s+(it|this|that|the\s+\w+)\s+(showing|using|joining|getting|returning)/i,
      /\bshould\s+(use|fetch|return|be|join|filter|group)\b/i,
      /\bcorrection\s+note\b/i,
      /\bnever\s+(use|do|join|select)\b/i,
      /\bdo\s+not\s+use\b/i,
      /\bdoesn'?t\s+match\b/i,
    ];
    return teachingPatterns.some(pattern => pattern.test(t));
  };
  
  // Save user teaching to knowledge base and offer re-run
  const handleUserTeaching = async (teachingText) => {
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: teachingText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    
    // Find last assistant message that ran a query (has SQL / queryResults / tables)
    const lastAssistant = [...messages].reverse().find(m => 
      m.type === 'assistant' && (m.sql || m.queryResults || m.tablesUsed)
    );
    const lastUserQuery = pendingQuery || messages.filter(m => m.type === 'user').slice(-2)[0]?.content || '';
    
    try {
      // 1) Save raw teaching (legacy path) — already extends learned_correction
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/save-teaching`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teaching: teachingText,
          context: lastUserQuery
        })
      });
      
      // 2) Auto-capture full correction with SQL + tables context
      //    so the AI learns the wrong→right delta, not just a free-form note
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge/capture-correction`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            user_message: teachingText,
            previous_question: lastUserQuery,
            previous_sql: lastAssistant?.sql || lastAssistant?.sqlExecuted || null,
            previous_tables: lastAssistant?.tablesUsed || null,
            category: 'chat_correction'
          })
        });
      } catch (capErr) {
        console.warn('[Teaching] capture-correction failed:', capErr);
      }
      
      // AI acknowledges and offers to re-run
      const ackMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `✅ **Got it.** I've saved this correction to my knowledge base — linked to your last question${lastAssistant?.sql ? ' and the SQL I generated' : ''}.\n\n> "${teachingText}"\n\nI'll apply this next time. Want me to retry the previous query with this in mind? Just say **"retry"** or rephrase the question.`,
        isTeachingAck: true,
        previousQuery: lastUserQuery,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, ackMessage]);
      
      toast.success('Correction saved — AI will use it next time.');
      
    } catch (error) {
      console.error('Failed to save teaching:', error);
      toast.error('Failed to save, but I noted your correction');
    }
  };

  // Two-Phase Query: Step 2 - Execute after confirmation (Legacy - now uses staged)
  const handleConfirmQuery = async () => {
    setShowEstimateDialog(false);
    // User message already added in handleEstimate
    setLoading(true);
    setThinkingStartTime(Date.now());
    
    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);
    
    // Detailed thinking steps like Emergent shows
    const progressSteps = [
      { text: 'Reading your question...', icon: '📖', delay: 0 },
      { text: 'Loading database schema...', icon: '📊', delay: 800 },
      { text: 'Checking table relationships...', icon: '🔗', delay: 1600 },
      { text: 'Identifying relevant tables...', icon: '🔍', delay: 2400 },
      { text: 'Verifying column names...', icon: '✓', delay: 3200 },
      { text: 'Building SQL query...', icon: '🛠️', delay: 4000 },
      { text: 'Executing on database...', icon: '⚡', delay: 5500 },
      { text: 'Analyzing results...', icon: '🧠', delay: 7000 },
    ];
    
    // Show steps progressively
    setThinkingSteps([]);
    let stepIndex = 0;
    const thinkingInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setThinkingSteps(prev => [...prev, progressSteps[stepIndex]]);
        setThinkingStatus(progressSteps[stepIndex].text);
        stepIndex++;
      }
    }, 1200);

    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      
      const response = await confirmAIQuery(pendingQuery, connectionId, conversationId, currentEstimate.estimate_id);
      
      // Check if cancelled
      if (controller.signal.aborted) {
        clearInterval(thinkingInterval);
        return;
      }
      
      clearInterval(thinkingInterval);
      setThinkingSteps([]);
      setAbortController(null);
      const totalTime = ((Date.now() - (thinkingStartTime || Date.now())) / 1000).toFixed(1);
      
      const aiMessage = {
        id: response.id,
        type: 'ai',
        content: response.analysis || response.response || 'No response',
        thinking: response.thinking || '',
        sql: response.sql,
        database: response.database,
        rowCount: response.row_count,
        results: response.results,
        success: response.success,
        error: response.error,
        timestamp: response.created_at,
        visualization: response.visualization,
        cached: response.cached || false,
        originalQuery: pendingQuery,
        processingTime: totalTime
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Play completion sound
      playCompletionSound();
      
      // Auto-expand chart if visualization exists
      if (response.visualization) {
        setExpandedCharts(prev => ({ ...prev, [response.id]: true }));
      }
    } catch (error) {
      clearInterval(thinkingInterval);
      setThinkingSteps([]);
      console.error('Query failed:', error);
      
      // Show better error message
      const errorText = error?.message || error?.toString() || 'Unknown error';
      toast.error('Query failed. See details below.');
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `❌ **Error:** ${errorText}\n\n**What went wrong:**\n- The AI service may be temporarily unavailable\n- The query might be too complex\n- There could be a connection issue\n\n**Try:**\n- Simplify your question\n- Wait a moment and try again\n- Check if the database connection is active`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setThinkingStatus(null);
      setThinkingStartTime(null);
      setThinkingSteps([]);
      setCurrentEstimate(null);
      setPendingQuery('');
    }
  };

  // Direct submit (skip estimate for simple queries or fallback)
  const handleDirectSubmit = async (queryText) => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const response = await sendAIQuery(queryText, connectionId, conversationId);
      
      const aiMessage = {
        id: response.id,
        type: 'ai',
        content: response.analysis || response.response || 'No response',
        sql: response.sql,
        database: response.database,
        rowCount: response.row_count,
        results: response.results,
        success: response.success,
        error: response.error,
        timestamp: response.created_at,
        visualization: response.visualization,
        cached: response.cached || false,
        originalQuery: queryText
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Play completion sound
      playCompletionSound();
      
      if (response.visualization) {
        setExpandedCharts(prev => ({ ...prev, [response.id]: true }));
      }
      
      if (response.cached) {
        toast.success('Instant result from cache', { duration: 2000 });
      }
    } catch (error) {
      console.error('Query failed:', error);
      toast.error('Failed to process your query. Please try again.');
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: 'I apologize, but I encountered an error processing your query. Please try again or check your connection settings.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Direct submit without adding user message (for fallback when message already added)
  const handleDirectSubmitNoMessage = async (queryText) => {
    setLoading(true);

    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const response = await sendAIQuery(queryText, connectionId, conversationId);
      
      const aiMessage = {
        id: response.id,
        type: 'ai',
        content: response.analysis || response.response || 'No response',
        sql: response.sql,
        database: response.database,
        rowCount: response.row_count,
        results: response.results,
        success: response.success,
        error: response.error,
        timestamp: response.created_at,
        visualization: response.visualization,
        cached: response.cached || false,
        originalQuery: queryText
      };

      setMessages(prev => [...prev, aiMessage]);
      playCompletionSound();
      
      if (response.visualization) {
        setExpandedCharts(prev => ({ ...prev, [response.id]: true }));
      }
    } catch (error) {
      console.error('Query failed:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: 'I apologize, but I encountered an error processing your query. Please try again or check your connection settings.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Refresh a cached query (force fresh data)
  const handleRefreshQuery = async (originalQuery) => {
    if (!originalQuery || loading) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: `[Refreshing] ${originalQuery}`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const response = await sendAIQuery(originalQuery, connectionId, conversationId, true);
      
      const aiMessage = {
        id: response.id,
        type: 'ai',
        content: response.analysis || response.response || 'No response',
        sql: response.sql,
        database: response.database,
        rowCount: response.row_count,
        results: response.results,
        success: response.success,
        error: response.error,
        timestamp: response.created_at,
        visualization: response.visualization,
        cached: false,
        originalQuery
      };
      setMessages(prev => [...prev, aiMessage]);
      
      if (response.visualization) {
        setExpandedCharts(prev => ({ ...prev, [response.id]: true }));
      }
      toast.success('Fresh results loaded');
    } catch (error) {
      toast.error('Refresh failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit feedback to teach the AI
  const handleSubmitFeedback = async (messageId, originalQuery, originalSql) => {
    const text = feedbackText[messageId]?.trim();
    if (!text) {
      toast.error('Please enter feedback text');
      return;
    }
    
    setFeedbackSending(true);
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      
      // Ensure we have valid data
      const queryText = originalQuery || 'Query feedback';
      const sqlText = originalSql || '';
      
      console.log('[Feedback] Submitting:', { messageId, queryText, sqlText: sqlText?.substring(0, 100), text: text?.substring(0, 100) });
      
      const result = await submitAIFeedback(messageId, queryText, sqlText, text, connectionId);
      
      console.log('[Feedback] Success:', result);
      toast.success('Thanks! AI will use this for future queries.');
      setFeedbackOpen(prev => ({ ...prev, [messageId]: false }));
      setFeedbackText(prev => ({ ...prev, [messageId]: '' }));
    } catch (error) {
      console.error('[Feedback] Error:', error?.response?.data || error.message || error);
      
      // Handle Pydantic validation errors (detail is an array)
      let errorMsg = 'Failed to save feedback';
      const detail = error?.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract message from first error
        const firstError = detail[0];
        errorMsg = firstError?.msg || firstError?.message || JSON.stringify(firstError);
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setFeedbackSending(false);
    }
  };

  // Quick feedback (thumbs up/down)
  const handleQuickFeedback = async (messageId, feedbackType, message) => {
    // If already submitted same feedback, ignore
    if (quickFeedback[messageId] === feedbackType) return;
    
    // If thumbs down, show reason input first
    if (feedbackType === 'thumbs_down' && !quickFeedbackReason && showQuickFeedbackReason !== messageId) {
      setShowQuickFeedbackReason(messageId);
      return;
    }
    
    try {
      await submitQuickFeedback(
        messageId,
        message.originalQuestion || message.content,
        message.sql,
        feedbackType,
        feedbackType === 'thumbs_down' ? quickFeedbackReason : null
      );
      
      setQuickFeedback(prev => ({ ...prev, [messageId]: feedbackType }));
      setShowQuickFeedbackReason(null);
      setQuickFeedbackReason('');
      
      if (feedbackType === 'thumbs_up') {
        toast.success('Thanks for the feedback!', { duration: 2000 });
      } else {
        toast.success('Feedback recorded. We\'ll improve!', { duration: 2000 });
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  // Retry AI Analysis
  const handleRetryAnalysis = async (messageId, message) => {
    if (retryingAnalysis[messageId]) return;
    
    setRetryingAnalysis(prev => ({ ...prev, [messageId]: true }));
    
    try {
      const result = await retryAIAnalysis(
        message.originalQuestion || message.content,
        message.sql,
        message.results || [],
        selectedAnalystRole
      );
      
      if (result.success) {
        // Update the message with new analysis
        setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              analysis: result.analysis,
              summaryStats: result.summary_stats,
              roleSpecificInsights: result.role_specific_insights || [],
              crossRoleAlerts: result.cross_role_alerts || [],
              anomalyInvestigation: result.anomaly_investigation,
              visualization: result.visualization,
              followUpQuestions: result.follow_up_questions || [],
              analysisRetried: true
            };
          }
          return msg;
        }));
        toast.success('AI Analysis refreshed successfully!');
      } else {
        toast.error(result.error || 'Failed to generate analysis');
      }
    } catch (error) {
      toast.error('Failed to retry analysis');
    } finally {
      setRetryingAnalysis(prev => ({ ...prev, [messageId]: false }));
    }
  };

  // Run query in background
  const handleRunInBackground = async () => {
    setShowEstimateDialog(false);
    
    try {
      const connectionId = selectedConnection === 'all' ? null : selectedConnection;
      const result = await submitBackgroundQuery(pendingQuery, connectionId, conversationId);
      
      if (result.success) {
        toast.success('Query running in background');
        setShowBackgroundPanel(true);
        fetchBackgroundQueries();
      } else {
        toast.error(result.error || 'Failed to start background query');
      }
    } catch (error) {
      toast.error('Failed to start background query');
    } finally {
      setCurrentEstimate(null);
      setPendingQuery('');
      setQuery('');
    }
  };

  // Cancel a background query
  const handleCancelQuery = async (queryId) => {
    try {
      await cancelQuery(queryId);
      toast.success('Query cancelled');
      fetchBackgroundQueries();
    } catch (error) {
      toast.error('Failed to cancel query');
    }
  };

  // ========================
  // QUERY TEMPLATES
  // ========================
  
  const handleSaveAsTemplate = (queryText) => {
    setTemplateForm({ name: '', description: '', category: 'general', query: queryText || query });
    setShowSaveTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    setSavingTemplate(true);
    try {
      await createQueryTemplate(
        templateForm.name,
        templateForm.query || query,
        templateForm.description,
        templateForm.category
      );
      toast.success('Template saved!');
      setShowSaveTemplateDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleUseTemplate = async (template) => {
    setQuery(template.query);
    setShowTemplates(false);
    try {
      await trackTemplateUsage(template.id);
    } catch (e) {
      // Silently fail - just tracking usage
    }
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      await deleteQueryTemplate(templateId);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleToggleFavorite = async (templateId, e) => {
    e.stopPropagation();
    try {
      await toggleFavoriteTemplate(templateId);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // ========================
  // CONTEXT SUMMARIZATION
  // ========================
  
  const handleSummarizeContext = async () => {
    if (messages.length === 0) {
      toast.error('No messages to summarize');
      return;
    }
    
    setSummarizing(true);
    
    try {
      // Build conversation summary request
      const conversationData = messages.map(m => ({
        type: m.type,
        content: m.content || m.query,
        sql: m.sql,
        rowCount: m.rowCount,
        summary: m.analysis?.summary || m.content?.substring(0, 200)
      }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/summarize-conversation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          messages: conversationData
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create a new conversation with the summary as context
        const newConvId = `conv-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        
        // Add summary message to new conversation
        const summaryMessage = {
          id: Date.now(),
          type: 'system',
          content: `📋 **Conversation Summary** (${messageCount} messages condensed)\n\n${data.summary}\n\n---\n*Previous conversation archived. You can continue asking questions with this context.*`,
          timestamp: new Date().toISOString(),
          isSummary: true,
          previousConversationId: conversationId,
          originalMessageCount: messageCount
        };
        
        // Reset to new conversation with summary
        setMessages([summaryMessage]);
        setConversationId(newConvId);
        setShowContextWarning(false);
        
        toast.success(`Conversation summarized! ${messageCount} messages → 1 summary`);
      } else {
        throw new Error('Failed to summarize');
      }
    } catch (error) {
      console.error('Summarization failed:', error);
      toast.error('Failed to summarize conversation');
    } finally {
      setSummarizing(false);
    }
  };
  
  const handleStartNewConversation = () => {
    const newConvId = `conv-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setMessages([]);
    setConversationId(newConvId);
    setShowContextWarning(false);
    toast.success('Started new conversation');
  };

  // ========================
  // EXPORT CONVERSATION
  // ========================
  
  const handleExportConversation = (format = 'markdown') => {
    if (messages.length === 0) {
      toast.error('No messages to export');
      return;
    }

    let content = '';
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `ai-query-conversation-${timestamp}`;

    if (format === 'markdown') {
      content = `# AI Query Conversation\n`;
      content += `**Date:** ${new Date().toLocaleString()}\n\n`;
      content += `---\n\n`;

      messages.forEach((msg, idx) => {
        if (msg.type === 'user') {
          content += `## 💬 User Query\n`;
          content += `${msg.content}\n\n`;
        } else {
          content += `## 🤖 AI Response\n`;
          content += `${msg.content}\n\n`;
          // SQL only for admins
          if (user?.role === 'admin' && msg.sql) {
            content += `### SQL Query\n\`\`\`sql\n${msg.sql}\n\`\`\`\n\n`;
          }
          if (msg.database) {
            content += `**Database:** ${msg.database}\n`;
          }
          if (msg.rowCount !== undefined) {
            content += `**Rows:** ${msg.rowCount}\n`;
          }
          if (msg.processingTime) {
            content += `**Processing Time:** ${msg.processingTime}s\n`;
          }
          content += `\n---\n\n`;
        }
      });

      const blob = new Blob([content], { type: 'text/markdown' });
      saveAs(blob, `${filename}.md`);
      toast.success('Conversation exported as Markdown');
    } else if (format === 'json') {
      const exportData = {
        exported_at: new Date().toISOString(),
        conversation_id: conversationId,
        messages: messages.map(msg => ({
          type: msg.type,
          content: msg.content,
          // SQL only for admins
          sql: user?.role === 'admin' ? msg.sql : undefined,
          database: msg.database,
          row_count: msg.rowCount,
          timestamp: msg.timestamp,
          processing_time: msg.processingTime
        }))
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      saveAs(blob, `${filename}.json`);
      toast.success('Conversation exported as JSON');
    }
  };

  // ========================
  // ANNOTATIONS
  // ========================
  
  const handleOpenAnnotation = async (queryId) => {
    setCurrentAnnotationQueryId(queryId);
    setAnnotationText('');
    setAnnotationType('note');
    
    // Fetch existing annotations
    try {
      const data = await getAnnotations(queryId);
      setAnnotations(prev => ({ ...prev, [queryId]: data.annotations || [] }));
    } catch (error) {
      console.error('Failed to fetch annotations:', error);
    }
    setShowAnnotationDialog(true);
  };

  const handleSaveAnnotation = async () => {
    if (!annotationText.trim()) {
      toast.error('Please enter annotation text');
      return;
    }
    try {
      const result = await createAnnotation(currentAnnotationQueryId, annotationText, annotationType);
      setAnnotations(prev => ({
        ...prev,
        [currentAnnotationQueryId]: [result.annotation, ...(prev[currentAnnotationQueryId] || [])]
      }));
      setAnnotationText('');
      toast.success('Annotation added');
    } catch (error) {
      toast.error('Failed to save annotation');
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      await deleteAnnotation(annotationId);
      setAnnotations(prev => ({
        ...prev,
        [currentAnnotationQueryId]: (prev[currentAnnotationQueryId] || []).filter(a => a.id !== annotationId)
      }));
      toast.success('Annotation deleted');
    } catch (error) {
      toast.error('Failed to delete annotation');
    }
  };

  // ========================
  // QUERY COMPARISON
  // ========================
  
  const handleCompareQuery = (queryText) => {
    setCompareQuery(queryText || query);
    setShowCompareDialog(true);
  };
  
  // Parse message content for clickable options (numbered lists, choices)
  const parseClickableOptions = (content) => {
    if (!content) return { text: content, options: [] };
    
    const options = [];
    let cleanedText = content;
    
    // Match numbered options like "1. Option text" or "1) Option text"
    const numberedPattern = /^(\d+)[.)]\s*(.+)$/gm;
    let match;
    while ((match = numberedPattern.exec(content)) !== null) {
      options.push({
        number: match[1],
        text: match[2].trim(),
        full: match[0]
      });
    }
    
    // Match lettered options like "a) Option" or "A. Option"
    const letteredPattern = /^([a-zA-Z])[.)]\s*(.+)$/gm;
    while ((match = letteredPattern.exec(content)) !== null) {
      options.push({
        number: match[1].toUpperCase(),
        text: match[2].trim(),
        full: match[0]
      });
    }
    
    // Match bullet options with clear choices
    const bulletPattern = /^[-•]\s*\*?\*?(.+?)\*?\*?:\s*(.+)$/gm;
    while ((match = bulletPattern.exec(content)) !== null) {
      options.push({
        number: '•',
        text: match[1].trim(),
        description: match[2].trim(),
        full: match[0]
      });
    }
    
    return { text: cleanedText, options };
  };
  
  // Render clickable options as buttons
  const renderClickableOptions = (options, context) => {
    if (!options || options.length === 0) return null;
    
    return (
      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500 mb-2">👆 Click to select:</p>
        <div className="flex flex-wrap gap-2">
          {options.map((opt, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              onClick={() => handleOptionClick(opt.text, context)}
              className="text-left h-auto py-2 px-3 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-medium">
                  {opt.number}
                </span>
                <span className="text-gray-700">{opt.text}</span>
              </span>
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const runComparisonQuery = async () => {
    if (!compareQuery.trim()) {
      toast.error('Please enter a query');
      return;
    }
    
    // Add time period context to the query
    const period1Query = `${compareQuery} for ${comparePeriod1}`;
    const period2Query = `${compareQuery} for ${comparePeriod2}`;
    
    setShowCompareDialog(false);
    
    // Run both queries sequentially and show results
    toast.info(`Running comparison: ${comparePeriod1} vs ${comparePeriod2}`);
    
    // Submit first query
    handleSubmit(null, `Compare ${comparePeriod1}: ${compareQuery}`);
  };

  // Export query results directly as Excel (client-side, no server needed)
  const handleExportResults = async (messageId, results, queryText) => {
    if (!results || results.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      const headers = Object.keys(results[0]);
      const wsData = [
        headers,
        ...results.map(row => headers.map(h => {
          const val = row[h];
          return val === null || val === undefined ? '' : val;
        }))
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Auto-width columns
      ws['!cols'] = headers.map(h => {
        let maxLen = h.length;
        results.slice(0, 50).forEach(row => {
          const val = row[h];
          if (val !== null && val !== undefined) {
            maxLen = Math.max(maxLen, String(val).length);
          }
        });
        return { wch: Math.min(maxLen + 2, 40) };
      });
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `query_${new Date().toISOString().split('T')[0]}_${Date.now()}.xlsx`;
      saveAs(blob, filename);
      toast.success('Excel file downloaded!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to generate Excel file');
    }
  };

  // FIX E: User clicked a disambiguation chip → save term_mapping AND re-submit the original question.
  // Stage 1 will now find the new mapping in the glossary and deterministically pick the right table.
  const handleTableClarificationPick = async (message, candidate) => {
    if (!message || !candidate || message.resolved) return;
    const pickedFullName = `${candidate.server}.${candidate.database}.${candidate.table}`;
    const term = message.ambiguousTerm || '';
    const originalQ = message.originalQuestion || '';

    // Mark this message resolved (visual feedback)
    setMessages(prev => prev.map(m =>
      m.id === message.id ? { ...m, resolved: true, pickedTable: pickedFullName } : m
    ));

    // Persist the mapping to the knowledge base
    try {
      await saveTermMapping({
        term,
        server: candidate.server,
        database: candidate.database,
        table: candidate.table,
        notes: `Auto-saved from disambiguation on "${originalQ.slice(0, 200)}"`,
        question: originalQ,
      });
      toast.success(`Saved: "${term}" → ${pickedFullName}`, { duration: 2500 });
    } catch (err) {
      console.error('saveTermMapping failed:', err);
      toast.error(`Saved your choice locally; couldn't persist mapping: ${err?.message || 'unknown error'}`);
    }

    // Re-run the original question — Stage 1 will now see the glossary entry
    if (originalQ) {
      setTimeout(() => {
        if (useSmartMode) {
          handleSmartQuery(originalQ);
        } else if (useAutonomousMode) {
          handleAutonomousQuery(originalQ);
        } else {
          handleStagedQuery(originalQ);
        }
        setPendingQuery(originalQ);
      }, 300);
    }
  };


  const handleSubmit = async (e, prefilledQuery = null) => {
    e?.preventDefault();
    const queryText = prefilledQuery || query.trim();
    if (!queryText) return;
    
    // Check if user is teaching/correcting the AI
    if (isUserTeaching(queryText)) {
      handleUserTeaching(queryText);
      return;
    }
    
    // Check if the last AI message was a clarification — user is answering it
    const lastAiMsg = [...messages].reverse().find(m => m.type === 'assistant');
    if (lastAiMsg?.isClarification && lastAiMsg.originalQuestion) {
      // Combine original question + user's clarification answer
      const combinedQuery = `${lastAiMsg.originalQuestion} (Clarification: ${queryText})`;
      if (useSmartMode) {
        handleSmartQuery(combinedQuery);
      } else if (useAutonomousMode) {
        handleAutonomousQuery(combinedQuery);
      } else {
        handleStagedQuery(combinedQuery);
      }
      setPendingQuery(combinedQuery);
      setQuery('');
      return;
    }
    
    // Use smart mode, autonomous mode, or staged process
    if (useSmartMode) {
      handleSmartQuery(queryText);
    } else if (useAutonomousMode) {
      handleAutonomousQuery(queryText);
    } else {
      handleStagedQuery(queryText);
    }
    setPendingQuery(queryText);
    setQuery('');
  };
  
  // Smart Two-Stage AI Query - Stage 1: Table Selection, Stage 2: Focused Query
  // Uses SSE streaming for real-time progress updates
  const handleSmartQuery = async (queryText) => {
    setLoading(true);
    setThinkingStartTime(Date.now());
    setThinkingSteps([{ 
      text: 'Starting Smart AI analysis...', 
      icon: '🎯',
      status: 'running',
      detail: null
    }]);
    
    // Clear previous query message ID to avoid skipping result event incorrectly
    window._lastQueryMessageId = null;
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    const usePreview = adminPreviewMode && user?.role === 'admin';
    
    // Reset rich AI thoughts at the start of each query
    setAiThoughts([]);
    
    // Handler for AI thinking events (rich content: reasoning, plan, table explanations)
    const handleAiThought = (data) => {
      console.log('[Smart AI Stream] AI Thought:', data.stage, data.label);
      setAiThoughts(prev => [...prev, {
        stage: data.stage,
        label: data.label,
        icon: data.icon || '🧠',
        content: data.content,
        elapsed: data.elapsed,
        timestamp: Date.now(),
      }]);
    };
    
    // Handler for streaming progress updates
    const handleProgress = (data) => {
      console.log('[Smart AI Stream] Progress:', data.message, data.elapsed || '');
      
      // Capture query_id for cancellation
      if (window._activeStreamQueryId) {
        setActiveQueryId(window._activeStreamQueryId);
      }
      
      // Convert server progress to UI steps with elapsed time
      const newStep = {
        text: `${data.message || data.stage || 'Processing...'}${data.elapsed ? ` (${data.elapsed})` : ''}`,
        icon: data.icon || '⚙️',
        status: data.status === 'completed' ? 'done' : 'running',
        detail: data.detail ? { ...data.detail, elapsed: data.elapsed } : (data.elapsed ? { elapsed: data.elapsed } : null)
      };
      
      // Update steps - add new step, mark running steps as done
      setThinkingSteps(prev => {
        // Find if a step with similar message exists
        const baseMsg = data.message?.split(' (')[0] || '';
        const existingIdx = prev.findIndex(s => s.text.startsWith(baseMsg));
        
        if (existingIdx >= 0 && data.status === 'completed') {
          // Update existing step
          const updated = [...prev];
          updated[existingIdx] = newStep;
          return updated;
        }
        
        // Mark previous running step as done and add new step
        const updated = prev.map(s => s.status === 'running' ? { ...s, status: 'done' } : s);
        return [...updated, newStep];
      });
    };
    
    // Handler for final result
    const handleResult = (result) => {
      console.log('[Smart AI Stream] Result:', result);
      
      // Mark all steps as done
      setThinkingSteps(prev => [
        ...prev.map(s => ({ ...s, status: 'done' })),
        { text: '✅ Done!', icon: '🎉', status: 'done' }
      ]);
      
      // Handle preview mode response (admin review before execution)
      if (result.preview) {
        const messageId = Date.now() + 1;
        const previewMessage = {
          id: messageId,
          type: 'assistant',
          content: result.stage2?.understanding || 'Here is my plan:',
          isPreview: true,
          stage1: result.stage1,
          stage2: result.stage2,
          query: result.query,
          sql: result.query?.sql,
          database: result.query?.database,
          server: result.query?.server,
          timestamp: new Date().toISOString(),
          originalQuestion: queryText
        };
        setMessages(prev => [...prev, previewMessage]);
        setAdminPreview({
          question: queryText,
          plan: result
        });
        
        // Auto-expand SQL for preview
        setExpandedDetails(prev => ({ ...prev, [messageId]: true }));
        setLoading(false);
        setTimeout(() => setThinkingSteps([]), 3000);
        return;
      }
      
      // Handle conversational/meta response (greetings, capability questions)
      // Backend bypassed the SQL pipeline and returned a friendly chat reply.
      if (result.is_conversational) {
        const messageId = Date.now() + 1;
        const conversationalMessage = {
          id: messageId,
          type: 'assistant',
          isConversational: true,
          content: result.content || "I'm here to help with your data questions. Try asking me about your reports or metrics.",
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, conversationalMessage]);
        setLoading(false);
        setTimeout(() => setThinkingSteps([]), 1500);
        return;
      }
      
      // Handle AI needs clarification (for vague queries)
      if (result.needs_clarification) {
        const messageId = Date.now() + 1;
        const suggestedQueries = result.suggested_queries || [];
        const clarificationQuestion = result.question_for_user || result.clarification_question || "I need some clarification before I can complete this query.";
        
        const clarificationMessage = {
          id: messageId,
          type: 'assistant',
          isClarification: true,
          content: result.partial_understanding || result.reasoning || clarificationQuestion,
          questionForUser: clarificationQuestion,
          suggestedQueries: suggestedQueries,
          clarificationContext: result.clarification_context || {},
          stage1: result.stage1,
          originalQuestion: result.original_question || queryText,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, clarificationMessage]);
        setLoading(false);
        setTimeout(() => setThinkingSteps([]), 3000);
        return;
      }

      // ===== FIX E: Handle TABLE-AMBIGUITY clarification (active learning) =====
      // AI flagged that an ambiguous term has multiple plausible tables.
      // Render clickable chips; on click we save a term_mapping AND re-submit.
      if (result.needs_table_clarification) {
        const messageId = Date.now() + 1;
        const tableClarificationMessage = {
          id: messageId,
          type: 'assistant',
          isTableClarification: true,
          ambiguousTerm: result.ambiguous_term || '',
          questionForUser: result.clarification_question || `Which table did you mean for "${result.ambiguous_term}"?`,
          candidates: result.candidates || [],
          reasoning: result.reasoning || '',
          originalQuestion: result.original_question || queryText,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, tableClarificationMessage]);
        setLoading(false);
        setTimeout(() => setThinkingSteps([]), 2000);
        return;
      }
      
      if (result.success) {
        // If we already received query_results, don't add another message
        // The result event is a fallback for when streaming doesn't work
        if (window._lastQueryMessageId) {
          console.log('[Smart AI Stream] Skipping result - already have query_results message');
          // Just update thinking steps to done
          setThinkingSteps(prev => [
            ...prev.map(s => ({ ...s, status: 'done' })),
            { text: '✅ Done!', icon: '🎉', status: 'done' }
          ]);
          setTimeout(() => setThinkingSteps([]), 3000);
          return;
        }
        
        const contextInfo = result.context_reduction 
          ? `\n\n_Context: ${result.context_reduction}_`
          : '';
        
        const messageId = Date.now() + 1;
        const aiMessage = {
          id: messageId,
          type: 'assistant',
          content: result.stage2?.understanding || 'Query executed successfully',
          results: result.results,
          query: result.query,
          sql: result.query?.sql,
          database: result.query?.database,
          server: result.query?.server,
          rowCount: result.total_rows,
          timestamp: new Date().toISOString(),
          isSmartMode: true,
          stage1: result.stage1,
          stage2: result.stage2,
          contextInfo,
          // Analyst Role Info
          analystRole: result.analyst_role,
          roleSpecificInsights: result.role_specific_insights || [],
          crossRoleAlerts: result.cross_role_alerts || [],
          // AI Analysis, Visualization & Follow-ups
          analysis: result.analysis,
          visualization: result.visualization,
          followUpQuestions: result.follow_up_questions || [],
          lookupInfo: result.lookup_info,
          // Data freshness & live query suggestion
          dataFreshness: result.data_freshness,
          liveQuerySuggestion: result.live_query_suggestion,
          // Zero results suggestion
          suggestion: result.suggestion,
          // Summary stats & anomaly investigation
          summaryStats: result.summary_stats,
          anomalyInvestigation: result.anomaly_investigation,
          originalQuestion: queryText,
          // AI Metadata for Top 10 Features
          confidence: result.stage2?.confidence || 'medium',
          confidenceReason: result.stage2?.confidence_reason || '',
          assumptions: result.stage2?.assumptions || [],
          limitations: result.stage2?.limitations || [],
          optimizationHints: result.stage2?.optimization_hints || [],
          tableExplanations: result.stage1?.table_explanations || [],
          crossServerWarning: result.stage1?.cross_server_warning,
          alternativeApproach: result.stage1?.alternative_approach,
          conversationContext: result.conversation_context,
          explainAnalysis: result.explain_analysis  // EXPLAIN results for query optimization
        };
        setMessages(prev => [...prev, aiMessage]);
        
        // Auto-expand tables by default
        setExpandedTables(prev => ({ ...prev, [messageId]: true }));
        
        // Auto-expand SQL for admin users
        if (user?.role === 'admin') {
          setExpandedDetails(prev => ({ ...prev, [messageId]: true }));
        }
        
        // Auto-expand chart if visualization exists
        if (result.visualization) {
          setExpandedCharts(prev => ({ ...prev, [messageId]: true }));
        }
        
        if (!conversationId && result.conversation_id) {
          setConversationId(result.conversation_id);
        }
        
        playCompletionSound();
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: `Smart query failed: ${result.error}`,
          error: true,
          timestamp: new Date().toISOString(),
          stage1_tables: result.stage1_tables,
          stage2_plan: result.stage2_plan,
          sql: result.sql
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      
      setLoading(false);
      setTimeout(() => setThinkingSteps([]), 3000);
    };
    
    // Handler for errors
    const handleError = (errorData) => {
      console.error('[Smart AI Stream] Error:', errorData);
      // errorData can be a string or an object with message, sql_attempted, etc.
      const errorMsg = typeof errorData === 'string' ? errorData : errorData.message || 'Query failed';
      const sqlAttempted = typeof errorData === 'object' ? errorData.sql_attempted : null;
      const stage1Tables = typeof errorData === 'object' ? errorData.stage1_tables : null;
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Error: ${errorMsg}`,
        error: true,
        sql: sqlAttempted,  // Include the SQL that was attempted
        stage1_tables: stage1Tables,
        timestamp: new Date().toISOString()
      };
      // Attach any AI thoughts that streamed before the error
      setAiThoughts(currentThoughts => {
        errorMessage.aiThoughts = currentThoughts;
        setMessages(prev => [...prev, errorMessage]);
        return [];
      });
      setThinkingSteps([{ text: 'Query failed', icon: '❌', status: 'done' }]);
      setLoading(false);
      setTimeout(() => setThinkingSteps([]), 3000);
    };
    
    // Handler for immediate query results (before AI analysis)
    const handleQueryResults = (data) => {
      console.log('[Smart AI Stream] Query Results (immediate):', data);
      
      // Get the EXPLAIN analysis from window (set by handleExplainAnalysis)
      const explainData = window._lastExplainAnalysis || null;
      
      // Show query results immediately - user doesn't have to wait for AI analysis
      const messageId = Date.now() + 1;
      // Snapshot+clear the AI thoughts that streamed in for this query, and attach them to the message
      setAiThoughts(currentThoughts => {
        const aiMessage = {
          id: messageId,
          type: 'assistant',
          content: data.stage2?.understanding || 'Query executed successfully',
          results: data.results,
          query: data.query,
          sql: data.query?.sql,
          database: data.query?.database,
          server: data.query?.server,
          rowCount: data.total_rows,
          timestamp: new Date().toISOString(),
          isSmartMode: true,
          stage1: data.stage1,
          stage2: data.stage2,
          analystRole: data.analyst_role,
          // EXPLAIN Analysis
          explainAnalysis: explainData,
          // Mark analysis as pending
          analysisPending: data.analysis_pending,
          // Zero results suggestion
          suggestion: data.suggestion,
          originalQuestion: queryText,
          // AI metadata
          confidence: data.stage2?.confidence || 'medium',
          assumptions: data.stage2?.assumptions || [],
          limitations: data.stage2?.limitations || [],
          // Live AI thoughts streamed during this query
          aiThoughts: currentThoughts,
        };
        setMessages(prev => [...prev, aiMessage]);
        return [];  // Reset thoughts buffer for next query
      });
      
      // Clear the EXPLAIN analysis state
      window._lastExplainAnalysis = null;
      setExplainAnalysis(null);
      
      // Store message ID for later analysis update
      window._lastQueryMessageId = messageId;
      
      // Auto-expand results table
      setExpandedTables(prev => ({ ...prev, [messageId]: true }));
      
      // Admin auto-expand SQL - DISABLED: SQL hidden by default
      // if (user?.role === 'admin') {
      //   setExpandedDetails(prev => ({ ...prev, [messageId]: true }));
      // }
      
      // Stop the loading spinner - user has their results!
      setLoading(false);
      playCompletionSound();
      
      // Update thinking steps to show analysis is in progress
      setThinkingSteps(prev => [
        ...prev.map(s => ({ ...s, status: 'done' })),
        { text: 'AI analyzing results...', icon: '🧠', status: 'running', detail: { elapsed: data.elapsed } }
      ]);
    };
    
    // Handler for EXPLAIN analysis (comes before query results)
    const handleExplainAnalysis = (data) => {
      console.log('[Smart AI Stream] EXPLAIN Analysis:', data);
      setExplainAnalysis(data);
      
      // Store in window for later message update
      window._lastExplainAnalysis = data;
      
      // Update thinking steps to show EXPLAIN results
      const analysis = data.analysis || {};
      const riskLevel = analysis.risk_level || 'low';
      const estimatedRows = analysis.estimated_rows || 0;
      
      const riskIcon = riskLevel === 'high' ? '⚠️' : (riskLevel === 'medium' ? '⚡' : '✅');
      const riskText = riskLevel === 'high' 
        ? `High risk: ~${estimatedRows.toLocaleString()} rows` 
        : riskLevel === 'medium'
          ? `Medium: ~${estimatedRows.toLocaleString()} rows`
          : `Optimized: ~${estimatedRows.toLocaleString()} rows`;
      
      setThinkingSteps(prev => [
        ...prev.filter(s => s.stage !== 'explain'),
        { text: riskText, icon: riskIcon, status: 'done', stage: 'explain' }
      ]);
      
      // Show toast for high risk queries
      if (riskLevel === 'high') {
        toast.warning(`⚠️ High risk query: may scan ${estimatedRows.toLocaleString()} rows`, { duration: 5000 });
      }
    };
    
    // Handler for AI analysis completion (may come later)
    const handleAnalysisComplete = (data) => {
      console.log('[Smart AI Stream] Analysis Complete:', data);
      
      // Update the existing message with analysis
      const messageId = window._lastQueryMessageId;
      if (messageId) {
        setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              analysis: data.analysis,
              visualization: data.visualization,
              followUpQuestions: data.follow_up_questions || [],
              analysisPending: false
            };
          }
          return msg;
        }));
        
        // Auto-expand chart if visualization exists
        if (data.visualization) {
          setExpandedCharts(prev => ({ ...prev, [messageId]: true }));
        }
      }
      
      // Mark analysis as done
      setThinkingSteps(prev => [
        ...prev.map(s => ({ ...s, status: 'done' })),
        { text: `Analysis complete (${data.elapsed || '?'})`, icon: '🎉', status: 'done' }
      ]);
      
      // Clear steps after a delay
      setTimeout(() => setThinkingSteps([]), 3000);
    };
    
    // Use streaming version for real-time progress
    smartAIQueryStream(
      queryText,
      conversationId,
      usePreview,
      selectedAnalystRole,
      handleProgress,
      handleResult,
      handleError,
      handleQueryResults,
      handleAnalysisComplete,
      handleExplainAnalysis,
      false,            // adminTrainingMode (default)
      handleAiThought   // new: live AI thinking events
    );
  };
  
  // Autonomous AI Query - AI has direct MongoDB access and figures everything out
  const handleAutonomousQuery = async (queryText) => {
    setLoading(true);
    setThinkingSteps([
      { text: 'AI is exploring the database...', icon: '🔍' },
      { text: 'Discovering servers and tables...', icon: '🗄️' }
    ]);
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const result = await autonomousAIQuery(queryText, conversationId);
      
      setThinkingSteps(prev => [
        ...prev,
        { text: `AI explored in ${result.iterations || '?'} steps`, icon: '🧠' }
      ]);
      
      if (result.success) {
        // Show exploration steps if available
        const explorationSummary = result.exploration_history?.length > 0
          ? `\n\n_Explored: ${result.exploration_history.map(h => h.action).join(' → ')}_`
          : '';
        
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          isAutonomous: true,
          content: result.analysis || result.understanding,
          understanding: result.understanding,
          plan: result.plan,
          sql: result.sql,
          server: result.server,
          database: result.database,
          results: result.results,
          rowCount: result.row_count,
          iterations: result.iterations,
          explorationHistory: result.exploration_history,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiMessage]);
        
        if (result.row_count !== undefined) {
          toast.success(`Query complete! ${result.row_count} rows (AI explored in ${result.iterations} steps)`);
        }
      } else {
        const errorMessage = {
          id: Date.now() + 2,
          type: 'ai',
          content: `❌ ${result.error || 'AI could not complete the query'}${result.raw_response ? '\n\n**Raw AI Response:**\n```\n' + result.raw_response + '\n```' : ''}${result.parse_error ? '\n\n**Parse Error:** ' + result.parse_error : ''}`,
          isError: true,
          explorationHistory: result.exploration_history,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        toast.error(result.error || 'Query failed');
      }
    } catch (error) {
      console.error('Autonomous query error:', error);
      const errorMessage = {
        id: Date.now() + 3,
        type: 'ai',
        content: `❌ Error: ${error.message}`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Query failed');
    } finally {
      setLoading(false);
      setThinkingSteps([]);
    }
  };

  const handleKeyDown = (e) => {
    // Enter = new line (default behavior)
    // Shift+Enter or Ctrl+Enter = send message
    if (e.key === 'Enter' && (e.shiftKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
    // Regular Enter just creates new line (no preventDefault needed)
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const suggestedQueries = [
    "How many logins today for brandId 31?",
    "Show top 10 users by wager amount",
    "Daily deposit trends last month",
    "Which provider has highest turnover?",
    "Win/loss ratio by brand"
  ];

  // Format a cell value for display - numbers get commas
  const formatCellValue = (val) => {
    if (val === null || val === undefined) return <span className="text-gray-400 italic">null</span>;
    if (typeof val === 'number') {
      // Integer: add commas
      if (Number.isInteger(val)) return val.toLocaleString();
      // Float: add commas + 2 decimals
      return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const str = String(val);
    // Check if string is a numeric value
    if (/^-?\d+(\.\d+)?$/.test(str.replace(/,/g, ''))) {
      const num = parseFloat(str.replace(/,/g, ''));
      if (!isNaN(num)) {
        if (Number.isInteger(num)) return num.toLocaleString();
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
    return str;
  };

  const formatMessage = (content) => {
    if (!content) return null;
    
    // Handle case where content is a JSON object or JSON string
    let textContent = content;
    if (typeof content === 'object') {
      // Extract message from JSON response (e.g., {type: "conversational", message: "..."})
      textContent = content.message || content.analysis || content.text || content.response || JSON.stringify(content);
    } else if (typeof content === 'string') {
      // Check if string is JSON that needs parsing
      const trimmed = content.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed);
          // Extract readable content from common JSON structures
          // Handle conversational type specifically
          if (parsed.type === 'conversational' && parsed.message) {
            textContent = parsed.message;
          } else if (parsed.message) {
            textContent = parsed.message;
          } else if (parsed.analysis) {
            textContent = parsed.analysis;
          } else if (parsed.text) {
            textContent = parsed.text;
          } else if (parsed.response) {
            textContent = parsed.response;
          } else if (parsed.type === 'clarification' && parsed.questions) {
            // Handle clarification responses
            textContent = parsed.message || 'I have some questions about your request:';
            if (Array.isArray(parsed.questions)) {
              textContent += '\n\n' + parsed.questions.map((q, i) => `${i + 1}. ${q}`).join('\n');
            }
          } else if (parsed.type === 'single' && (parsed.explanation || parsed.thinking)) {
            // Handle query plan response - show thinking first, then explanation
            if (parsed.thinking) {
              textContent = '🧠 **My reasoning:**\n' + parsed.thinking + '\n\n';
            }
            if (parsed.explanation) {
              textContent += parsed.explanation;
            }
          } else if (parsed.type === 'federated' && (parsed.explanation || parsed.thinking)) {
            // Handle federated query response
            if (parsed.thinking) {
              textContent = '🧠 **My reasoning:**\n' + parsed.thinking + '\n\n';
            }
            if (parsed.explanation) {
              textContent += parsed.explanation;
            }
          } else if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            // For other objects, create a readable format or use first string value
            const stringVals = Object.entries(parsed).filter(([k, v]) => typeof v === 'string' && v.length > 10);
            if (stringVals.length > 0) {
              textContent = stringVals.map(([k, v]) => v).join('\n\n');
            }
          }
        } catch (e) {
          // Not valid JSON, use as-is
        }
      }
    }
    
    // Format standalone numbers in text with commas (e.g., 1234567 → 1,234,567)
    const formatInlineNumbers = (text) => {
      if (typeof text !== 'string') return text;
      return text.replace(/\b(\d{4,}(?:\.\d+)?)\b/g, (match) => {
        const num = parseFloat(match);
        if (isNaN(num)) return match;
        if (Number.isInteger(num)) return num.toLocaleString();
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      });
    };
    
    if (typeof textContent !== 'string') {
      textContent = String(textContent);
    }
    
    return textContent.split('\n').map((line, i) => {
      if (line.startsWith('```')) return null;
      const formatted = formatInlineNumbers(line);
      if (line.startsWith('# ')) {
        return <h3 key={i} className="text-lg font-semibold text-gray-900 mt-4 mb-2">{formatInlineNumbers(line.slice(2))}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h4 key={i} className="text-base font-semibold text-gray-900 mt-3 mb-1">{formatInlineNumbers(line.slice(3))}</h4>;
      }
      if (line.startsWith('### ')) {
        return <h5 key={i} className="text-sm font-semibold text-gray-900 mt-2 mb-1">{formatInlineNumbers(line.slice(4))}</h5>;
      }
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={i} className="ml-4 text-gray-700">{formatInlineNumbers(line.slice(2))}</li>;
      }
      if (line.match(/^\d+\. /)) {
        return <li key={i} className="ml-4 text-gray-700 list-decimal">{formatInlineNumbers(line.replace(/^\d+\. /, ''))}</li>;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-gray-700 mb-1">{formatted}</p>;
    });
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple': return 'text-green-600 bg-green-50 border-green-200';
      case 'moderate': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'complex': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] p-6" data-testid="ai-query-page">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              AI Query Interface
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Ask questions about your database in natural language
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Current Mode Badge */}
            <Badge variant="outline" className="text-xs">
              {useSmartMode ? '⚡ Smart AI' : useAutonomousMode ? '🔮 Full Context' : '📋 Staged'}
            </Badge>
            {/* Current Analyst Badge */}
            <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
              {Array.isArray(analystRoles) && analystRoles.find(r => r.id === selectedAnalystRole)?.icon} {Array.isArray(analystRoles) ? analystRoles.find(r => r.id === selectedAnalystRole)?.name || 'Analyst' : 'Analyst'}
            </Badge>
          </div>
        </div>

      {/* Chat Area */}
      <Card className="flex-1 bg-white border-gray-200 flex flex-col overflow-hidden min-w-0">
        <ScrollArea className="flex-1 p-6 overflow-x-hidden">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="p-4 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-6">
                <Sparkles className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                What would you like to know?
              </h2>
              <p className="text-gray-500 max-w-md mb-8">
                Ask me anything about your data. I'll estimate the query time first, then run it after your approval.
              </p>
              
              {/* Suggested Queries */}
              <div className="w-full max-w-2xl">
                <div className="flex items-center gap-2 mb-4 justify-center">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-gray-500">Try asking:</span>
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestedQueries.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setQuery(q);
                        textareaRef.current?.focus();
                      }}
                      className="px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm text-gray-700 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all"
                      data-testid={`suggested-query-btn-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Players — one-click to re-run a risk report */}
              {recentPlayers && recentPlayers.length > 0 && (
                <div className="w-full max-w-2xl mt-8" data-testid="recent-players-section">
                  <div className="flex items-center gap-2 mb-3 justify-center">
                    <span className="text-emerald-500">🛡️</span>
                    <span className="text-sm text-gray-500">Recent players (1-click risk report):</span>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {recentPlayers.map((p, i) => {
                      const brandLabel = p.brand_name || `brand ${p.player_brand_id}`;
                      const prompt = `Help me analyse risk behavior of the player :${p.player_username} from brand ${brandLabel}`;
                      return (
                        <button
                          key={`${p.player_username}-${p.player_brand_id}`}
                          onClick={() => {
                            setQuery(prompt);
                            textareaRef.current?.focus();
                          }}
                          className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 hover:bg-emerald-100 hover:border-emerald-300 transition-all flex items-center gap-1.5"
                          title={`member_id ${p.player_member_id} · queried ${p.query_count || 1}×`}
                          data-testid={`recent-player-btn-${i}`}
                        >
                          <span className="font-semibold">{p.player_username}</span>
                          <span className="text-emerald-500">·</span>
                          <span className="text-emerald-600">{brandLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 w-full overflow-x-hidden">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-4 ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up w-full`}
                >
                  {message.type === 'ai' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                  )}
                  
                  <div className={`${message.type === 'user' ? 'max-w-[80%]' : 'max-w-[90%] min-w-0'} ${message.type === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'} p-4 overflow-hidden`}>
                    {message.type === 'user' ? (
                      <p className="text-white break-words whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      <div className="ai-response w-full" style={{ maxWidth: 'calc(100vw - 450px)' }}>
                        {/* Cache badge */}
                        {message.cached && (
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid={`cache-badge-${message.id}`}>
                              <Zap className="w-3 h-3" />
                              Instant (cached result)
                            </span>
                            <button
                              onClick={() => handleRefreshQuery(message.originalQuery)}
                              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#D4AF37] transition-colors px-2 py-0.5 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200"
                              title="Re-run query with fresh data"
                              data-testid={`refresh-query-${message.id}`}
                              disabled={loading}
                            >
                              <RefreshCw className="w-3 h-3" />
                              Refresh
                            </button>
                          </div>
                        )}
                        
                        {/* Processing time badge */}
                        {message.processingTime && !message.cached && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-0.5">
                              <Clock className="w-3 h-3" />
                              Processed in {message.processingTime}s
                            </span>
                          </div>
                        )}
                        
                        {/* STAGED: Understanding Stage */}
                        {message.isStaged && message.stage === 'understanding' && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-2 text-blue-700 font-semibold mb-3">
                                <span className="text-lg">📖</span>
                                <span>Step 1: Understanding Your Request</span>
                              </div>
                              <p className="text-gray-800 mb-4">{message.content}</p>
                              
                              {/* Intended Report - NEW */}
                              {message.data?.intended_report && (
                                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                                  <p className="text-xs font-medium text-green-700 mb-1">📊 Report I'll generate:</p>
                                  <p className="text-sm text-green-800">{message.data.intended_report}</p>
                                </div>
                              )}
                              
                              {message.data?.data_needed?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Data I'll look for:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {message.data.data_needed.map((d, i) => (
                                      <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{d}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {message.data?.potential_tables?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Tables I might use:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {message.data.potential_tables.map((t, i) => (
                                      <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono">{t}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {message.data?.uncertainties?.length > 0 && (
                                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
                                  <p className="text-xs font-medium text-amber-700 mb-1">⚠️ Things I'm unsure about:</p>
                                  <ul className="text-xs text-amber-800 list-disc list-inside">
                                    {message.data.uncertainties.map((u, i) => (
                                      <li key={i}>{u}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {message.data?.clarifying_questions?.length > 0 && (
                                <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded">
                                  <p className="text-xs font-medium text-purple-700 mb-1">❓ I need to ask you:</p>
                                  <ul className="text-xs text-purple-800 list-disc list-inside">
                                    {message.data.clarifying_questions.map((q, i) => (
                                      <li key={i}>{q}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {/* ALWAYS show confirmation buttons */}
                              {awaitingUserInput && aiStage === 'understanding' && (
                                <div className="mt-4 pt-3 border-t border-blue-200">
                                  <p className="text-sm text-gray-600 mb-3">Shall I proceed with this?</p>
                                  <div className="flex gap-2 mb-3">
                                    <Button size="sm" onClick={handleApproveStage} className="bg-green-500 hover:bg-green-600">
                                      <Check className="w-4 h-4 mr-1" /> Yes, get the report
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => {}} className="text-amber-600 border-amber-200">
                                      <Edit className="w-4 h-4 mr-1" /> Let me clarify
                                    </Button>
                                  </div>
                                  <div className="flex gap-2">
                                    <Input
                                      value={userCorrectionInput}
                                      onChange={(e) => setUserCorrectionInput(e.target.value)}
                                      placeholder="Type any corrections or additional details..."
                                      className="flex-1 text-sm"
                                      onKeyDown={(e) => e.key === 'Enter' && userCorrectionInput && handleUserCorrection(userCorrectionInput)}
                                    />
                                    <Button size="sm" onClick={() => handleUserCorrection(userCorrectionInput)} disabled={!userCorrectionInput}>
                                      Send
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* STAGED: Planning Stage */}
                        {message.isStaged && message.stage === 'planning' && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
                              <div className="flex items-center gap-2 text-amber-700 font-semibold mb-3">
                                <span className="text-lg">🗺️</span>
                                <span>Step 2: Query Plan</span>
                              </div>
                              <p className="text-gray-800 mb-4">{message.content}</p>
                              
                              {message.data?.tables_to_use?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-2">Tables & Columns:</p>
                                  
                                  {/* Show if tables were auto-corrected */}
                                  {message.data?.tables_corrected && (
                                    <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                      ✓ I auto-corrected the table locations to match your actual schema!
                                    </div>
                                  )}
                                  
                                  <div className="space-y-2">
                                    {message.data.tables_to_use.map((t, i) => (
                                      <div key={i} className={`text-xs bg-white border rounded p-2 ${t._corrected ? 'border-green-300 bg-green-50' : ''}`}>
                                        <span className="font-mono font-medium text-amber-700">{t.server}.{t.database}.{t.table}</span>
                                        {t._corrected && (
                                          <span className="ml-2 text-green-600 text-xs">(corrected from {t._original_server}.{t._original_database})</span>
                                        )}
                                        <span className="text-gray-500 ml-2">({t.purpose})</span>
                                        <div className="text-gray-600 mt-1">Columns: {t.columns?.join(', ')}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* SQL Preview - ADMIN ONLY */}
                              {user?.role === 'admin' && message.data?.sql_preview && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">SQL Preview:</p>
                                  <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto font-mono">
                                    {message.data.sql_preview}
                                  </pre>
                                </div>
                              )}
                              
                              {message.data?.alternative_approaches?.length > 0 && (
                                <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded">
                                  <p className="text-xs font-medium text-purple-700 mb-1">🔀 Alternative approaches:</p>
                                  <ul className="text-xs text-purple-800 list-decimal list-inside">
                                    {message.data.alternative_approaches.map((a, i) => (
                                      <li key={i} className="mb-1">{a}</li>
                                    ))}
                                  </ul>
                                  <p className="text-xs text-purple-600 mt-2">Which approach would you prefer?</p>
                                </div>
                              )}
                              
                              {message.data?.assumptions?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Assumptions I'm making:</p>
                                  <ul className="text-xs text-gray-600 list-disc list-inside">
                                    {message.data.assumptions.map((a, i) => (
                                      <li key={i}>{a}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {/* Approval/Correction buttons */}
                              {awaitingUserInput && aiStage === 'planning' && (
                                <div className="mt-4 pt-3 border-t border-amber-200">
                                  <div className="flex gap-2 mb-2">
                                    <Button size="sm" onClick={handleApproveStage} className="bg-green-500 hover:bg-green-600">
                                      <Check className="w-4 h-4 mr-1" /> Execute this plan
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-amber-600 border-amber-200">
                                      Choose different approach
                                    </Button>
                                  </div>
                                  <div className="flex gap-2">
                                    <Input
                                      value={userCorrectionInput}
                                      onChange={(e) => setUserCorrectionInput(e.target.value)}
                                      placeholder="Type which approach or any corrections..."
                                      className="flex-1 text-sm"
                                    />
                                    <Button size="sm" onClick={() => handleUserCorrection(userCorrectionInput)}>Send</Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* STAGED: Needs Clarification - AI is asking for help */}
                        {message.isStaged && message.stage === 'needs_clarification' && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-300 rounded-lg">
                              <div className="flex items-center gap-2 text-orange-700 font-semibold mb-3">
                                <span className="text-lg">🤔</span>
                                <span>I Need Your Help</span>
                              </div>
                              
                              {/* What's blocking */}
                              <p className="text-gray-800 mb-3">{message.content}</p>
                              
                              {/* Likely cause if available */}
                              {message.likelyCause && (
                                <div className="mb-3 p-2 bg-orange-100 rounded text-sm text-orange-800">
                                  <strong>Likely cause:</strong> {message.likelyCause}
                                </div>
                              )}
                              
                              {/* The question */}
                              <div className="mb-4 p-3 bg-white border-2 border-orange-300 rounded-lg">
                                <p className="text-sm font-medium text-orange-800 mb-1">❓ My question:</p>
                                <p className="text-gray-900">{message.question}</p>
                              </div>
                              
                              {/* Failed SQL if available - ADMIN ONLY */}
                              {user?.role === 'admin' && message.failedSql && (
                                <details className="mb-3">
                                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    Show attempted SQL
                                  </summary>
                                  <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                    {message.failedSql}
                                  </pre>
                                </details>
                              )}
                              
                              {/* Suggested fix if available */}
                              {message.suggestedFix && (
                                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
                                  <strong className="text-green-700">💡 Suggestion:</strong> 
                                  <span className="text-green-800 ml-1">{message.suggestedFix}</span>
                                </div>
                              )}
                              
                              {/* Input area for user's answer */}
                              {awaitingUserInput && aiStage === 'needs_clarification' && (
                                <div className="mt-4 pt-3 border-t border-orange-200">
                                  <p className="text-sm text-gray-600 mb-2">
                                    Your answer will be saved to my knowledge base for future queries:
                                  </p>
                                  <div className="flex gap-2">
                                    <Input
                                      value={userCorrectionInput}
                                      onChange={(e) => setUserCorrectionInput(e.target.value)}
                                      placeholder="e.g., status=1 means 'success', status=2 means 'pending'..."
                                      className="flex-1 text-sm"
                                      onKeyDown={(e) => e.key === 'Enter' && userCorrectionInput && handleUserCorrection(userCorrectionInput)}
                                    />
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleUserCorrection(userCorrectionInput)} 
                                      disabled={!userCorrectionInput}
                                      className="bg-orange-500 hover:bg-orange-600"
                                    >
                                      <GraduationCap className="w-4 h-4 mr-1" /> Teach & Retry
                                    </Button>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-2">
                                    💾 I'll remember this for all future queries!
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* STAGED: Complete - Results */}
                        {message.isStaged && message.stage === 'complete' && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
                              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
                                <span className="text-lg">✅</span>
                                <span>Step 3: Results</span>
                                {message.rowCount !== undefined && (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded ml-2">
                                    {message.rowCount} rows
                                  </span>
                                )}
                              </div>
                              
                              {/* Show if we learned something */}
                              {message.learned && (
                                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                                  <GraduationCap className="w-4 h-4 inline mr-1" />
                                  {message.learned}
                                </div>
                              )}
                              
                              <div className="text-gray-800">
                                {formatMessage(message.content)}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* AUTONOMOUS AI: Results with exploration history */}
                        {message.isAutonomous && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                              <div className="flex items-center gap-2 text-purple-700 font-semibold mb-3">
                                <span className="text-lg">🤖</span>
                                <span>Autonomous AI</span>
                                {message.iterations && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded ml-2">
                                    {message.iterations} exploration steps
                                  </span>
                                )}
                                {message.rowCount !== undefined && (
                                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded ml-2">
                                    {message.rowCount} rows
                                  </span>
                                )}
                              </div>
                              
                              {/* Understanding */}
                              {message.understanding && (
                                <div className="mb-3 p-2 bg-white border border-purple-100 rounded">
                                  <p className="text-xs font-medium text-purple-600 mb-1">Understanding:</p>
                                  <p className="text-sm text-gray-700">{message.understanding}</p>
                                </div>
                              )}
                              
                              {/* Plan */}
                              {message.plan && (
                                <div className="mb-3 p-2 bg-white border border-purple-100 rounded">
                                  <p className="text-xs font-medium text-purple-600 mb-1">Plan:</p>
                                  <p className="text-sm text-gray-700">{message.plan}</p>
                                </div>
                              )}
                              
                              {/* SQL - ADMIN ONLY */}
                              {user?.role === 'admin' && message.sql && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">
                                    SQL on {message.server}.{message.database}:
                                  </p>
                                  <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto font-mono">
                                    {message.sql}
                                  </pre>
                                </div>
                              )}
                              
                              {/* Analysis */}
                              <div className="text-gray-800">
                                {formatMessage(message.content)}
                              </div>
                              
                              {/* Exploration History */}
                              {message.explorationHistory?.length > 0 && (
                                <details className="mt-3">
                                  <summary className="text-xs text-purple-600 cursor-pointer hover:text-purple-800">
                                    🔍 Show AI's exploration steps ({message.explorationHistory.length})
                                  </summary>
                                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                                    {message.explorationHistory.map((step, i) => (
                                      <div key={i} className="text-gray-600">
                                        {i + 1}. <code className="bg-gray-200 px-1 rounded">{step.action}</code>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Admin Training Mode - Show EXPLAIN + plan and allow approval/feedback */}
                        {message.isPreview && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                              <div className="flex items-center gap-2 text-purple-700 font-medium mb-3 text-sm">
                                <Edit className="w-4 h-4" />
                                🎓 Admin Training Mode - Review Before Executing
                              </div>
                              
                              {/* DBA Query Match Indicator */}
                              {message.stage1?.dba_query_match && (
                                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-700">
                                    Using DBA Query: <strong>{message.stage1.dba_query_match}</strong>
                                  </span>
                                </div>
                              )}
                              
                              {/* Stage 1 Reasoning */}
                              {message.stage1?.reasoning && (
                                <div className="mb-3 p-3 bg-white/70 border border-purple-100 rounded">
                                  <p className="text-xs font-medium text-purple-600 mb-1">🧠 AI Reasoning (Table Selection)</p>
                                  <p className="text-sm text-gray-700">{message.stage1.reasoning}</p>
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {message.stage1.selected_tables?.map((table, idx) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">{table}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Stage 2 Understanding & Plan */}
                              {message.stage2?.understanding && (
                                <div className="mb-3 p-3 bg-white/70 border border-purple-100 rounded">
                                  <p className="text-xs font-medium text-purple-600 mb-1">📋 AI Understanding</p>
                                  <p className="text-sm text-gray-700">{message.stage2.understanding}</p>
                                </div>
                              )}
                              
                              {/* SQL Preview - ADMIN ONLY */}
                              {user?.role === 'admin' && message.sql && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1">
                                    📝 Planned SQL Query ({message.server}.{message.database}):
                                  </p>
                                  <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto font-mono">
                                    {message.sql}
                                  </pre>
                                </div>
                              )}
                              
                              {/* EXPLAIN Analysis - Show prominently for admin training */}
                              {message.explainAnalysis && (
                                <div className={`mb-3 p-3 rounded border ${
                                  message.explainAnalysis.analysis?.risk_level === 'high' 
                                    ? 'bg-red-50 border-red-200' 
                                    : message.explainAnalysis.analysis?.risk_level === 'medium'
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-green-50 border-green-200'
                                }`}>
                                  <p className="text-xs font-medium mb-2 flex items-center gap-2">
                                    {message.explainAnalysis.analysis?.risk_level === 'high' && <AlertCircle className="w-4 h-4 text-red-500" />}
                                    {message.explainAnalysis.analysis?.risk_level === 'medium' && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                                    {message.explainAnalysis.analysis?.risk_level === 'low' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                    <span className={
                                      message.explainAnalysis.analysis?.risk_level === 'high' ? 'text-red-700' :
                                      message.explainAnalysis.analysis?.risk_level === 'medium' ? 'text-yellow-700' : 'text-green-700'
                                    }>
                                      EXPLAIN Analysis: {message.explainAnalysis.analysis?.risk_level?.toUpperCase()} Risk
                                    </span>
                                  </p>
                                  <div className="text-xs text-gray-600 space-y-1">
                                    <p>Estimated Rows: <strong>{message.explainAnalysis.analysis?.estimated_rows?.toLocaleString() || 'Unknown'}</strong></p>
                                    {message.explainAnalysis.warnings?.map((warn, idx) => (
                                      <p key={idx} className="text-yellow-700">⚠️ {warn}</p>
                                    ))}
                                    {message.explainAnalysis.recommendations?.map((rec, idx) => (
                                      <p key={idx} className="text-blue-600">💡 {rec}</p>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Training Feedback Input */}
                              <div className="mb-3">
                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                  💬 Feedback to Train AI (optional):
                                </label>
                                <Textarea
                                  placeholder="e.g., 'For login queries, always use market_report table' or 'When asking about brand performance, filter by agent_user_id is NULL'"
                                  className="text-sm h-16"
                                  value={adminTrainingFeedback}
                                  onChange={(e) => setAdminTrainingFeedback(e.target.value)}
                                  data-testid="admin-training-feedback"
                                />
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="mt-4 pt-3 border-t border-purple-200 flex items-center gap-3 flex-wrap">
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      setLoading(true);
                                      // Execute the confirmed query with optional feedback
                                      const result = await executeConfirmedQuery(
                                        message.server,
                                        message.database,
                                        message.sql,
                                        message.originalQuestion,
                                        conversationId,
                                        adminTrainingFeedback || null
                                      );
                                      
                                      if (result.success) {
                                        // Update the message with results
                                        setMessages(prev => prev.map(m => 
                                          m.id === message.id ? {
                                            ...m,
                                            isPreview: false,
                                            results: result.results,
                                            totalRows: result.total_rows
                                          } : m
                                        ));
                                        toast.success(`Query executed: ${result.total_rows} rows returned`);
                                        if (adminTrainingFeedback) {
                                          toast.success('Feedback saved for AI training!');
                                        }
                                      } else {
                                        toast.error(result.error || 'Query execution failed');
                                      }
                                    } catch (err) {
                                      toast.error(err.message || 'Failed to execute query');
                                    } finally {
                                      setLoading(false);
                                      setAdminTrainingFeedback('');
                                    }
                                  }}
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                  data-testid="execute-confirmed-btn"
                                  disabled={loading}
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  {loading ? 'Executing...' : 'Run Query'}
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (adminTrainingFeedback.trim()) {
                                      // Save feedback and re-run with the feedback as context
                                      setQuery(adminTrainingFeedback);
                                      setAdminTrainingFeedback('');
                                      toast.info('Re-running with your feedback...');
                                      handleSmartQuery(message.originalQuestion + ' [ADMIN FEEDBACK: ' + adminTrainingFeedback + ']');
                                    } else {
                                      toast.warning('Please enter feedback first');
                                    }
                                  }}
                                  data-testid="apply-feedback-btn"
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Re-run with Feedback
                                </Button>
                                
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setAdminPreview(null);
                                    setAdminTrainingFeedback('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                              <p className="mt-2 text-xs text-gray-500">
                                💡 Your feedback helps train the AI to handle similar questions better in the future.
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Teaching Acknowledgment with Re-run Button */}
                        {message.isTeachingAck && (
                          <div className="mb-4">
                            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                              <div className="text-gray-800">
                                {formatMessage(message.content)}
                              </div>
                              {message.previousQuery && (
                                <div className="mt-4 pt-3 border-t border-green-200">
                                  <p className="text-xs text-gray-600 mb-2">Now that I've learned this, would you like me to try again?</p>
                                  <Button
                                    size="sm"
                                    onClick={() => handleStagedQuery(message.previousQuery)}
                                    className="bg-blue-500 hover:bg-blue-600"
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Re-run: "{message.previousQuery.substring(0, 50)}{message.previousQuery.length > 50 ? '...' : ''}"
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Conversational / meta reply (greetings, capability questions) — chat-style, no SQL card */}
                        {message.isConversational && (
                          <div
                            data-testid="ai-conversational-message"
                            className="mb-3 p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
                          >
                            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-indigo-600">
                              <Sparkles className="w-4 h-4" />
                              MyAIAnalysis
                            </div>
                            <div className="prose prose-sm max-w-none text-gray-800">
                              {formatMessage(message.content)}
                            </div>
                          </div>
                        )}
                        
                        {/* AI Thought Process — rich thinking events streamed during the query */}
                        {message.aiThoughts && message.aiThoughts.length > 0 && (
                          <details
                            data-testid="ai-thought-process"
                            className="mb-3 group bg-gradient-to-br from-indigo-50/60 to-purple-50/60 border border-indigo-200 rounded-lg overflow-hidden"
                            open
                          >
                            <summary className="cursor-pointer px-4 py-2.5 flex items-center justify-between gap-2 hover:bg-indigo-100/40 transition-colors">
                              <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                                <span className="text-base">🧠</span>
                                <span>AI's Thought Process</span>
                                <Badge variant="outline" className="ml-1 text-[10px] bg-white/70 border-indigo-300 text-indigo-700">
                                  {message.aiThoughts.length} thought{message.aiThoughts.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <span className="text-xs text-indigo-500 group-open:rotate-180 transition-transform">▼</span>
                            </summary>
                            <div className="px-4 py-3 space-y-3 border-t border-indigo-200/70">
                              {message.aiThoughts.map((thought, idx) => (
                                <div
                                  key={idx}
                                  data-testid={`ai-thought-${idx}`}
                                  className="bg-white/80 backdrop-blur-sm border border-indigo-100 rounded-md px-3 py-2.5"
                                >
                                  <div className="flex items-center gap-2 mb-1.5 text-xs font-medium text-indigo-800">
                                    <span className="text-sm">{thought.icon || '🧠'}</span>
                                    <span>{thought.label}</span>
                                    {thought.elapsed && (
                                      <span className="ml-auto text-[10px] text-gray-400 font-normal">
                                        {thought.elapsed}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-700 leading-relaxed">
                                    {typeof thought.content === 'string' ? (
                                      <div className="whitespace-pre-wrap">{thought.content}</div>
                                    ) : Array.isArray(thought.content) ? (
                                      <ul className="space-y-1 pl-1">
                                        {thought.content.map((item, i) => (
                                          <li key={i} className="flex gap-2">
                                            <span className="text-indigo-400">•</span>
                                            <span className="flex-1">
                                              {typeof item === 'string'
                                                ? item
                                                : item && typeof item === 'object'
                                                  ? (
                                                    <span>
                                                      {item.step && <strong>Step {item.step}: </strong>}
                                                      {item.server && <code className="text-[10px] bg-indigo-50 px-1 rounded">{item.server}.{item.database}</code>}
                                                      {item.table && <code className="text-[10px] bg-indigo-50 px-1 rounded">{item.table}</code>}
                                                      {(item.purpose || item.why) && <span className="ml-1.5 text-gray-600">— {item.purpose || item.why}</span>}
                                                    </span>
                                                  )
                                                  : JSON.stringify(item)
                                              }
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <div className="space-y-1.5">
                                        {Object.entries(thought.content).map(([k, v]) => (
                                          <div key={k} className="flex flex-col gap-0.5">
                                            <div className="text-[10px] uppercase tracking-wide text-indigo-500 font-semibold">{k.replace(/_/g, ' ')}</div>
                                            {Array.isArray(v) ? (
                                              v.length === 0 ? (
                                                <div className="text-[11px] text-gray-400 italic">(none)</div>
                                              ) : (
                                                <ul className="list-disc list-inside text-[11px] text-gray-700">
                                                  {v.map((item, i) => (
                                                    <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
                                                  ))}
                                                </ul>
                                              )
                                            ) : typeof v === 'string' && (k.toLowerCase().includes('sql') || v.length > 120) ? (
                                              <pre className="text-[10px] bg-gray-50 border border-gray-200 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all text-gray-800 leading-snug">{v}</pre>
                                            ) : (
                                              <div className="text-[11px] text-gray-700 whitespace-pre-wrap break-words">{String(v ?? '—')}</div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                        
                        {/* AI Needs Clarification - Show Suggested Queries */}
                        {message.isClarification && message.suggestedQueries && message.suggestedQueries.length > 0 && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2 text-blue-700 font-medium mb-3 text-sm">
                              <Lightbulb className="w-4 h-4" />
                              Did you mean one of these?
                            </div>
                            <div className="space-y-2">
                              {message.suggestedQueries.map((query, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setQuery(query);
                                    if (useSmartMode) {
                                      handleSmartQuery(query);
                                    } else {
                                      handleStagedQuery(query);
                                    }
                                  }}
                                  disabled={loading}
                                  className="w-full text-left p-3 bg-white hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors group disabled:opacity-50"
                                  data-testid={`suggested-query-${idx}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-700 rounded-full text-xs font-bold group-hover:bg-blue-200">
                                      {idx + 1}
                                    </span>
                                    <span className="text-sm text-gray-700">{query}</span>
                                    <ChevronRight className="w-4 h-4 text-blue-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* AI Needs Clarification - Natural Chat Style */}
                        {message.isClarification && (
                          <div className="mb-4" data-testid={`clarification-msg-${message.id}`}>
                            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {message.content && <p className="mb-2">{message.content}</p>}
                              <p>{message.questionForUser}</p>
                            </div>
                            {message.suggestedQueries && message.suggestedQueries.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {message.suggestedQueries.map((sq, sqIdx) => (
                                  <button
                                    key={sqIdx}
                                    onClick={() => { setQuery(sq); }}
                                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-full transition-colors text-gray-600"
                                    data-testid={`clarification-suggestion-${sqIdx}`}
                                  >
                                    {sq}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* === FIX E: Table-Ambiguity Clarification with Auto-Learning Chips === */}
                        {message.isTableClarification && (
                          <div
                            className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg"
                            data-testid={`table-clarification-${message.id}`}
                          >
                            <div className="flex items-start gap-2 mb-3">
                              <span className="text-xl">🤔</span>
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-amber-900 mb-1">
                                  Which table did you mean for{' '}
                                  <span className="px-1.5 py-0.5 bg-amber-200 rounded font-mono">
                                    {message.ambiguousTerm || 'this term'}
                                  </span>
                                  ?
                                </div>
                                <div className="text-xs text-amber-700">
                                  {message.reasoning || 'Multiple tables could match this term. Pick one — I’ll remember for next time so I never ask again.'}
                                </div>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {(message.candidates || []).map((cand, ci) => (
                                <button
                                  key={ci}
                                  onClick={() => handleTableClarificationPick(message, cand)}
                                  disabled={message.resolved}
                                  className={`w-full text-left p-3 rounded-lg border transition-all
                                    ${message.resolved && message.pickedTable === `${cand.server}.${cand.database}.${cand.table}`
                                      ? 'bg-emerald-100 border-emerald-400 cursor-default'
                                      : message.resolved
                                        ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                                        : 'bg-white border-amber-300 hover:border-amber-500 hover:shadow-md cursor-pointer'}`}
                                  data-testid={`table-clarification-chip-${ci}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-mono text-gray-900 truncate">
                                        <span className="text-blue-600">{cand.server}</span>
                                        <span className="text-gray-400">.</span>
                                        <span className="text-purple-600">{cand.database}</span>
                                        <span className="text-gray-400">.</span>
                                        <span className="text-gray-900 font-semibold">{cand.table}</span>
                                      </div>
                                      {cand.why && (
                                        <div className="text-xs text-gray-600 mt-0.5">{cand.why}</div>
                                      )}
                                    </div>
                                    {message.resolved && message.pickedTable === `${cand.server}.${cand.database}.${cand.table}` && (
                                      <span className="text-emerald-600 text-lg shrink-0">✓</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                            {message.resolved && (
                              <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5">
                                <span>🧠</span>
                                <span>Saved to your knowledge base. Re-running your question…</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Non-staged messages: AI Thinking/Reasoning */}
                        {!message.isStaged && !message.isTeachingAck && !message.isConversational && message.thinking && (
                          <details className="mb-4 group">
                            <summary className="cursor-pointer p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-t-lg text-sm flex items-center gap-2 text-blue-700 font-medium hover:bg-blue-100 transition-colors">
                              <span>🧠</span> 
                              <span>AI Reasoning Process</span>
                              <ChevronRight className="w-4 h-4 ml-auto transition-transform group-open:rotate-90" />
                            </summary>
                            <div className="p-4 bg-blue-50/50 border border-t-0 border-blue-200 rounded-b-lg">
                              <div className="text-blue-800 whitespace-pre-wrap text-xs font-mono leading-relaxed">
                                {message.thinking}
                              </div>
                            </div>
                          </details>
                        )}
                        
                        {/* Non-staged Analysis/Answer - Clear Results Box */}
                        {!message.isStaged && !message.isTeachingAck && !message.isConversational && (
                          <div className="mb-3 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
                                <Sparkles className="w-4 h-4" />
                                Analysis Results
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Confidence Score Badge */}
                                {message.confidence && (
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${
                                      message.confidence === 'high' 
                                        ? 'bg-green-50 text-green-700 border-green-200' 
                                        : message.confidence === 'medium' 
                                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                                          : 'bg-red-50 text-red-700 border-red-200'
                                    }`}
                                    title={message.confidenceReason || 'AI confidence level'}
                                  >
                                    <Target className="w-3 h-3 mr-1" />
                                    {message.confidence === 'high' ? '95%' : message.confidence === 'medium' ? '75%' : '50%'} confident
                                  </Badge>
                                )}
                                {/* Analyst Role Badge */}
                                {message.analystRole && (
                                  <Badge 
                                    variant="outline" 
                                    className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                                  >
                                    {message.analystRole.icon} {message.analystRole.name}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Quick Feedback (Thumbs Up/Down) */}
                            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-emerald-200">
                              <span className="text-xs text-gray-500">Was this helpful?</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleQuickFeedback(message.id, 'thumbs_up', message)}
                                className={`h-7 px-2 ${quickFeedback[message.id] === 'thumbs_up' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:text-green-600'}`}
                                data-testid={`thumbs-up-${message.id}`}
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleQuickFeedback(message.id, 'thumbs_down', message)}
                                className={`h-7 px-2 ${quickFeedback[message.id] === 'thumbs_down' ? 'bg-red-100 text-red-700' : 'text-gray-500 hover:text-red-600'}`}
                                data-testid={`thumbs-down-${message.id}`}
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </Button>
                              
                              {/* Thumbs Down Reason Input */}
                              {showQuickFeedbackReason === message.id && (
                                <div className="flex items-center gap-2 ml-2 flex-1">
                                  <Input
                                    placeholder="What could be improved? (optional)"
                                    value={quickFeedbackReason}
                                    onChange={(e) => setQuickFeedbackReason(e.target.value)}
                                    className="h-7 text-xs flex-1"
                                    data-testid={`feedback-reason-${message.id}`}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleQuickFeedback(message.id, 'thumbs_down', message)}
                                    className="h-7 px-2 text-xs"
                                  >
                                    Submit
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setShowQuickFeedbackReason(null);
                                      setQuickFeedbackReason('');
                                    }}
                                    className="h-7 px-2"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {/* AI Analysis Summary - FIRST, most important */}
                            {message.analysis && message.analysis.summary && (
                              <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    AI Summary
                                  </p>
                                  {/* Show Retry button if analysis failed or is unavailable */}
                                  {(message.analysis.summary.includes('temporarily unavailable') || 
                                    message.analysis.summary.includes('AI analysis') ||
                                    !message.analysis.insights?.length) && !message.analysisRetried && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRetryAnalysis(message.id, message)}
                                      disabled={retryingAnalysis[message.id]}
                                      className="h-6 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                      data-testid={`retry-analysis-${message.id}`}
                                    >
                                      {retryingAnalysis[message.id] ? (
                                        <>
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          Retrying...
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="w-3 h-3 mr-1" />
                                          Retry
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                                <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-table:text-xs prose-th:bg-emerald-100 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-td:border prose-th:border prose-table:border-collapse prose-table:my-2 prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                                  {(() => {
                                    const summary = message.analysis.summary || '';
                                    const isRiskReport = /risk report|player risk|player profile.*kyc|scored areas/i.test(summary);
                                    return (
                                      <>
                                        {isRiskReport && (
                                          <div
                                            className="mb-3 px-3 py-2 rounded-md border-l-4 border-emerald-500 bg-emerald-50 text-emerald-900 text-xs font-semibold tracking-wide"
                                            data-testid="risk-report-banner"
                                          >
                                            🛡️ Structured Risk Report — the AI's full answer is below. The raw data table further down is supporting evidence only.
                                          </div>
                                        )}
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                          {summary}
                                        </ReactMarkdown>
                                      </>
                                    );
                                  })()}
                                </div>
                                
                                {/* Key Insights inline */}
                                {message.analysis.insights && message.analysis.insights.length > 0 && (
                                  <div className="mt-3 pt-2 border-t border-emerald-200">
                                    <ul className="space-y-1">
                                      {message.analysis.insights.map((insight, idx) => (
                                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                          <span className="text-emerald-500 mt-0.5">→</span>
                                          {insight}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Assumptions & Limitations (Collapsible - hidden by default) */}
                            {((message.assumptions && message.assumptions.length > 0) || 
                              (message.limitations && message.limitations.length > 0) ||
                              message.crossServerWarning) && (
                              <details className="mb-3">
                                <summary className="cursor-pointer text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1 py-1">
                                  <Info className="w-3 h-3" />
                                  <span className="font-medium">AI Transparency</span>
                                  <span className="text-gray-400 ml-1">({(message.assumptions?.length || 0) + (message.limitations?.length || 0)} notes)</span>
                                </summary>
                                <div className="mt-2 p-2 bg-amber-50/50 border border-amber-100 rounded text-xs">
                                  {message.assumptions && message.assumptions.length > 0 && (
                                    <div className="mb-2">
                                      <span className="text-gray-600 font-medium">Assumptions:</span>
                                      <ul className="ml-2 mt-1 space-y-0.5">
                                        {message.assumptions.map((a, idx) => (
                                          <li key={idx} className="text-gray-500">• {a}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {message.limitations && message.limitations.length > 0 && (
                                    <div className="mb-2">
                                      <span className="text-gray-600 font-medium">Limitations:</span>
                                      <ul className="ml-2 mt-1 space-y-0.5">
                                        {message.limitations.map((l, idx) => (
                                          <li key={idx} className="text-gray-500">• {l}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {message.crossServerWarning && (
                                    <div className="text-amber-700 mt-1">
                                      <Shield className="w-3 h-3 inline mr-1" />
                                      {message.crossServerWarning}
                                    </div>
                                  )}
                                </div>
                              </details>
                            )}
                            
                            <div className="text-gray-700 text-sm">
                              {formatMessage(message.content)}
                            </div>
                            
                            {/* Show Retry Analysis when no analysis at all but has results */}
                            {!message.analysis && message.results?.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-emerald-200">
                                <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded">
                                  <div className="flex items-center gap-2 text-amber-700">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm">AI analysis wasn't generated for this query</span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRetryAnalysis(message.id, message)}
                                    disabled={retryingAnalysis[message.id]}
                                    className="h-7 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid={`retry-analysis-missing-${message.id}`}
                                  >
                                    {retryingAnalysis[message.id] ? (
                                      <>
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        Generating...
                                      </>
                                    ) : (
                                      <>
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        Generate AI Analysis
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Summary Statistics / Grand Totals */}
                            {message.summaryStats && Object.keys(message.summaryStats).length > 0 && (
                              <div className="mt-4 pt-3 border-t border-emerald-200">
                                <p className="text-sm font-medium text-emerald-800 mb-2">📊 Summary Totals</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {Object.entries(message.summaryStats).map(([col, stats]) => (
                                    <div key={col} className="p-2 bg-white/70 rounded border border-emerald-100">
                                      <p className="text-xs text-gray-500 truncate" title={col}>{col}</p>
                                      <p className="text-lg font-semibold text-emerald-700">
                                        {typeof stats.total === 'number' ? stats.total.toLocaleString() : stats.total}
                                      </p>
                                      {stats.average && (
                                        <p className="text-xs text-gray-500">
                                          avg: {typeof stats.average === 'number' ? stats.average.toLocaleString(undefined, {maximumFractionDigits: 2}) : stats.average}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Trend Analysis */}
                            {message.analysis?.trend && (
                              <div className="mt-3 p-3 rounded-lg border ${
                                message.analysis.trend.is_concerning 
                                  ? 'bg-red-50 border-red-200' 
                                  : message.analysis.trend.direction === 'increasing' 
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-blue-50 border-blue-200'
                              }">
                                <div className="flex items-center gap-2">
                                  {message.analysis.trend.direction === 'increasing' && (
                                    <span className="text-green-600">📈</span>
                                  )}
                                  {message.analysis.trend.direction === 'decreasing' && (
                                    <span className="text-red-600">📉</span>
                                  )}
                                  {message.analysis.trend.direction === 'stable' && (
                                    <span className="text-blue-600">➡️</span>
                                  )}
                                  <span className={`text-sm font-medium ${
                                    message.analysis.trend.is_concerning ? 'text-red-700' : 'text-gray-700'
                                  }`}>
                                    {message.analysis.trend.description}
                                    {message.analysis.trend.percentage_change && (
                                      <span className="ml-1">
                                        ({message.analysis.trend.percentage_change > 0 ? '+' : ''}{message.analysis.trend.percentage_change}%)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {/* Role-Specific Insights */}
                            {message.roleSpecificInsights && message.roleSpecificInsights.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-indigo-200">
                                <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-indigo-700 font-medium mb-2 text-sm">
                                    <span>{message.analystRole?.icon || '📊'}</span>
                                    {message.analystRole?.name || 'Analyst'} Insights
                                  </div>
                                  <ul className="space-y-1">
                                    {message.roleSpecificInsights.map((insight, idx) => (
                                      <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                        <span className="text-indigo-500 mt-1">→</span>
                                        {insight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                            
                            {/* Cross-Role Alerts */}
                            {message.crossRoleAlerts && message.crossRoleAlerts.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-medium text-gray-600 mb-2">🔔 Other Teams Might Want to Know:</p>
                                <div className="flex flex-wrap gap-2">
                                  {message.crossRoleAlerts.map((alert, idx) => (
                                    <div 
                                      key={idx} 
                                      className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs"
                                    >
                                      <span className="font-medium text-amber-800">{alert.role}:</span>
                                      <span className="text-gray-600 ml-1">{alert.alert}</span>
                                      {alert.suggested_query && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            // Switch role and run query
                                            const roleMap = {
                                              'Risk Analyst': 'risk_compliance_analyst',
                                              'Marketing': 'marketing_analyst',
                                              'Operations': 'operations_analyst',
                                              'Finance': 'finance_analyst'
                                            };
                                            const newRole = roleMap[alert.role] || 'all';
                                            setSelectedAnalystRole(newRole);
                                            setQuery(alert.suggested_query);
                                            handleSmartQuery(alert.suggested_query);
                                          }}
                                          className="ml-2 h-5 px-2 text-[10px] text-amber-700 hover:bg-amber-100"
                                        >
                                          Investigate →
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Anomaly Investigation - Auto Investigation Agent */}
                            {message.anomalyInvestigation && (
                              <div className="mt-4 pt-3 border-t border-red-200">
                                <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 text-red-700 font-medium text-sm">
                                      <AlertTriangle className="w-4 h-4" />
                                      Anomaly Detected
                                      <Badge variant="outline" className={`ml-2 text-xs ${
                                        message.anomalyInvestigation.severity === 'high' ? 'bg-red-100 text-red-700 border-red-300' :
                                        message.anomalyInvestigation.severity === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                        'bg-blue-100 text-blue-700 border-blue-300'
                                      }`}>
                                        {message.anomalyInvestigation.severity} severity
                                      </Badge>
                                    </div>
                                    
                                    {/* Auto Investigation Button */}
                                    {!investigations[message.id] && (
                                      <Button
                                        size="sm"
                                        onClick={() => triggerInvestigation(message.id, message)}
                                        className="bg-red-500 hover:bg-red-600 text-white"
                                        data-testid={`auto-investigate-${message.id}`}
                                      >
                                        <Search className="w-4 h-4 mr-2" />
                                        Auto Investigate Root Cause
                                      </Button>
                                    )}
                                  </div>
                                  
                                  <p className="text-sm text-gray-800 mb-3">
                                    {message.anomalyInvestigation.detected_issue}
                                  </p>
                                  
                                  {/* Investigation Running */}
                                  {investigations[message.id]?.status === 'running' && (
                                    <div className="p-4 bg-white/80 border border-blue-200 rounded-lg">
                                      <div className="flex items-center gap-3">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                        <div>
                                          <p className="text-sm font-medium text-blue-700">🔍 AI Investigator Running...</p>
                                          <p className="text-xs text-gray-600">{investigations[message.id]?.progress}</p>
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-2">
                                        Running diagnostic queries and analyzing results to find the root cause...
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* Investigation Results */}
                                  {investigations[message.id]?.status === 'complete' && investigations[message.id]?.result && (
                                    <div className="mt-3 space-y-4">
                                      {/* Summary */}
                                      <div className="p-3 bg-white/80 border border-emerald-200 rounded-lg">
                                        <p className="text-sm font-medium text-emerald-800 mb-2">📋 Investigation Summary</p>
                                        <p className="text-sm text-gray-700">{investigations[message.id].result.summary}</p>
                                      </div>
                                      
                                      {/* Health Checks */}
                                      {investigations[message.id].result.health_checks?.length > 0 && (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                          {investigations[message.id].result.health_checks.map((check, idx) => (
                                            <div key={idx} className={`p-2 rounded border text-xs ${
                                              check.status === 'healthy' ? 'bg-green-50 border-green-200' :
                                              check.status === 'concerning' ? 'bg-red-50 border-red-200' :
                                              'bg-gray-50 border-gray-200'
                                            }`}>
                                              <div className="flex items-center gap-1 font-medium">
                                                {check.status === 'healthy' && <span className="text-green-600">✓</span>}
                                                {check.status === 'concerning' && <span className="text-red-600">⚠</span>}
                                                {check.metric}
                                              </div>
                                              <p className="text-gray-600 text-[10px] mt-0.5">{check.detail}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      
                                      {/* Root Cause */}
                                      {investigations[message.id].result.root_cause?.identified && (
                                        <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-300 rounded-lg">
                                          <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                                            <Lightbulb className="w-4 h-4" />
                                            Root Cause Identified
                                            <Badge variant="outline" className="ml-2 text-xs bg-emerald-100 text-emerald-700">
                                              {investigations[message.id].result.root_cause.confidence} confidence
                                            </Badge>
                                          </div>
                                          <p className="text-sm text-gray-800 mb-3">
                                            {investigations[message.id].result.root_cause.cause}
                                          </p>
                                          
                                          {/* Evidence Chain */}
                                          {investigations[message.id].result.root_cause.evidence?.length > 0 && (
                                            <div className="mb-3">
                                              <p className="text-xs font-medium text-emerald-700 mb-1">Evidence:</p>
                                              <ul className="space-y-0.5">
                                                {investigations[message.id].result.root_cause.evidence.map((e, idx) => (
                                                  <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                                                    <span className="text-emerald-500">→</span> {e}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Recommendations */}
                                      {investigations[message.id].result.recommendations?.length > 0 && (
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                          <p className="text-xs font-medium text-blue-800 mb-2">💡 Recommendations</p>
                                          <ul className="space-y-1">
                                            {investigations[message.id].result.recommendations.map((rec, idx) => (
                                              <li key={idx} className="text-xs text-gray-700 flex items-start gap-2">
                                                <span className="text-blue-500">{idx + 1}.</span> {rec}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Investigation Error */}
                                  {investigations[message.id]?.status === 'error' && (
                                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
                                      <p className="text-sm text-red-700">Investigation failed: {investigations[message.id]?.error}</p>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => triggerInvestigation(message.id, message)}
                                        className="mt-2"
                                      >
                                        Retry Investigation
                                      </Button>
                                    </div>
                                  )}
                                  
                                  {/* Manual Investigation Options (fallback) */}
                                  {!investigations[message.id] && message.anomalyInvestigation.diagnostic_queries?.length > 0 && (
                                    <details className="mt-3">
                                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                        Or investigate manually (click to expand)
                                      </summary>
                                      <div className="mt-2 space-y-2">
                                        {message.anomalyInvestigation.diagnostic_queries.map((dq, idx) => (
                                          <Button
                                            key={idx}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              setQuery(dq.query);
                                              handleSmartQuery(dq.query);
                                            }}
                                            className="w-full justify-start text-left h-auto py-2 px-3 text-xs bg-white hover:bg-red-50"
                                          >
                                            <div>
                                              <span className="font-medium text-gray-800">{dq.description}</span>
                                              <span className="block text-gray-500 text-[10px]">{dq.query}</span>
                                            </div>
                                          </Button>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Data Freshness Badge & Live Query Option */}
                            {message.dataFreshness && (
                              <div className="mt-3 pt-3 border-t border-emerald-200">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <Badge 
                                    variant={message.dataFreshness.status === 'fresh' ? 'default' : message.dataFreshness.status === 'recent' ? 'secondary' : 'outline'}
                                    className={`text-xs ${
                                      message.dataFreshness.status === 'fresh' ? 'bg-green-500' : 
                                      message.dataFreshness.status === 'recent' ? 'bg-blue-500' : 
                                      'bg-amber-100 text-amber-800 border-amber-300'
                                    }`}
                                  >
                                    <Clock className="w-3 h-3 mr-1" />
                                    {message.dataFreshness.label} (as of {message.dataFreshness.latest_date})
                                  </Badge>
                                  
                                  {/* Report Table Indicator */}
                                  {message.stage1?.is_report_table && (
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                      <Layers className="w-3 h-3 mr-1" />
                                      Pre-aggregated Report
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Live Query Suggestion */}
                                {message.liveQuerySuggestion && (
                                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-xs text-amber-800 mb-2">
                                      {message.liveQuerySuggestion.message}
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        // Add "live" keyword to force live query
                                        const liveQuery = `Get live/real-time data: ${message.originalQuestion}`;
                                        setQuery(liveQuery);
                                        handleSmartQuery(liveQuery);
                                      }}
                                      className="text-xs bg-white hover:bg-amber-100 border-amber-300"
                                      data-testid={`get-live-data-${message.id}`}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" />
                                      Get Real-Time Data
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Clickable options if present in message */}
                            {(() => {
                              const { options } = parseClickableOptions(message.content);
                              return options.length > 0 ? renderClickableOptions(options, message.content) : null;
                            })()}
                            
                            {/* AI Debug Panel - Shows reasoning process */}
                            {(message.stage1 || message.stage2) && (
                              <div className="mt-4 border-t border-gray-200 pt-3">
                                <button
                                  onClick={() => setExpandedDetails(prev => ({ 
                                    ...prev, 
                                    [`debug-${message.id}`]: !prev[`debug-${message.id}`] 
                                  }))}
                                  className="flex items-center gap-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                  data-testid={`toggle-debug-${message.id}`}
                                >
                                  <Code className="w-3.5 h-3.5" />
                                  {expandedDetails[`debug-${message.id}`] ? 'Hide' : 'Show'} AI Debug Info
                                  <ChevronRight className={`w-3 h-3 transition-transform ${expandedDetails[`debug-${message.id}`] ? 'rotate-90' : ''}`} />
                                </button>
                                
                                {expandedDetails[`debug-${message.id}`] && (
                                  <div className="mt-3 space-y-3 text-xs">
                                    {/* Stage 1: Table Selection */}
                                    {message.stage1 && (
                                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="font-semibold text-blue-800 mb-2">📊 Stage 1: Table Selection</p>
                                        {message.stage1.reasoning && (
                                          <p className="text-gray-700 mb-2">{message.stage1.reasoning}</p>
                                        )}
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {message.stage1.selected_tables?.map((table, idx) => (
                                            <Badge key={idx} variant="outline" className="text-xs bg-white">
                                              {table}
                                            </Badge>
                                          ))}
                                        </div>
                                        <p className="text-gray-500">
                                          Context: {message.stage1.context_size?.toLocaleString() || '?'} chars
                                          {message.stage1.is_report_table && ' • Uses pre-aggregated report table'}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* Stage 2: SQL Generation */}
                                    {message.stage2 && (
                                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <p className="font-semibold text-green-800 mb-2">🔧 Stage 2: SQL Generation</p>
                                        {message.stage2.understanding && (
                                          <p className="text-gray-700 mb-2">
                                            <span className="font-medium">Understanding:</span> {message.stage2.understanding}
                                          </p>
                                        )}
                                        {message.stage2.plan && (
                                          <p className="text-gray-700 mb-2">
                                            <span className="font-medium">Plan:</span> {message.stage2.plan}
                                          </p>
                                        )}
                                        <p className="text-gray-500">
                                          Context: {message.stage2.context_size?.toLocaleString() || '?'} chars
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* SQL Query - ADMIN ONLY */}
                                    {user?.role === 'admin' && message.sql && (
                                      <div className="p-3 bg-gray-900 rounded-lg">
                                        <p className="font-semibold text-gray-300 mb-2">📝 Generated SQL</p>
                                        <pre className="text-green-400 whitespace-pre-wrap text-xs overflow-x-auto">
                                          {message.sql}
                                        </pre>
                                      </div>
                                    )}
                                    
                                    {/* Query Target */}
                                    {message.server && message.database && (
                                      <div className="flex items-center gap-2 text-gray-600">
                                        <Database className="w-3 h-3" />
                                        <span>Target: {message.server}.{message.database}</span>
                                        {message.rowCount !== undefined && (
                                          <span className="text-green-600">• {message.rowCount} rows returned</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Zero Results - Show SQL attempted and suggestions */}
                        {message.rowCount === 0 && (
                          <div className="mt-4 pt-3 border-t border-amber-100 bg-amber-50/50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-amber-100 rounded-full">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="flex-1 space-y-3">
                                <div>
                                  <p className="font-medium text-amber-800">No data returned</p>
                                  <p className="text-sm text-amber-700 mt-1">
                                    {message.suggestion?.message || "The query executed successfully but returned no matching records."}
                                  </p>
                                  {message.suggestion?.reason && (
                                    <p className="text-xs text-amber-600 mt-1 italic">
                                      AI Analysis: {message.suggestion.reason}
                                    </p>
                                  )}
                                </div>
                                
                                {/* SQL Query Attempted - ADMIN ONLY */}
                                {user?.role === 'admin' && (
                                  <div className="bg-white/80 rounded border border-amber-200 p-3">
                                    <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                                      <Code className="w-3 h-3" />
                                      SQL Query Attempted:
                                    </p>
                                    <pre className="text-xs bg-gray-900 text-green-400 p-2 rounded overflow-x-auto max-h-32 font-mono">
                                      {message.sql || message.suggestion?.sql_attempted || "SQL not available"}
                                    </pre>
                                  </div>
                                )}
                                
                                {/* Tables Used - ADMIN ONLY */}
                                {user?.role === 'admin' && message.suggestion?.tables_used && (
                                  <div className="text-xs text-gray-600">
                                    <span className="font-medium">Tables queried: </span>
                                    {message.suggestion.tables_used.join(', ')}
                                  </div>
                                )}
                                
                                {/* Diagnosis-aware retry chips */}
                                {message.suggestion?.diagnosis_kind === 'future_date' && message.originalQuestion && (
                                  <div className="bg-purple-50 border border-purple-200 rounded p-2.5" data-testid="future-date-chips">
                                    <p className="text-xs font-medium text-purple-800 mb-1.5">📅 The date filter is in the future. Did you mean a previous year?</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {[1, 2].map((delta) => {
                                        const y = new Date().getFullYear() - delta;
                                        return (
                                          <button
                                            key={delta}
                                            data-testid={`retry-with-year-${y}`}
                                            disabled={loading}
                                            onClick={() => {
                                              const retryPrompt = `${message.originalQuestion}\n\n(Use year ${y} for the date filter — the previous attempt referenced a future date.)`;
                                              handleSmartQuery(retryPrompt);
                                            }}
                                            className="text-xs px-3 py-1 bg-white hover:bg-purple-100 active:bg-purple-200 border border-purple-300 text-purple-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                          >
                                            ↻ Retry with {y}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Fallback-used badge */}
                                {message.suggestion?.fallback_used && (
                                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1.5" data-testid="fallback-used-badge">
                                    <span>🔄</span>
                                    <span>
                                      Tried {message.suggestion.fallback_tier === 2 ? 'Tier-1 fix + Tier-2 raw source' : 'Tier-1 auto-fix'} — both empty.
                                    </span>
                                  </div>
                                )}
                                
                                {/* Alternative Tables Suggested - clickable retry */}
                                {message.suggestion?.alternative_tables && message.suggestion.alternative_tables.length > 0 && (
                                  <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                    <p className="text-xs font-medium text-blue-700 mb-1.5">AI suggests trying these tables instead — click one to retry:</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {message.suggestion.alternative_tables.map((table, idx) => (
                                        <button
                                          key={idx}
                                          data-testid={`retry-with-table-${idx}`}
                                          disabled={loading}
                                          onClick={() => {
                                            const originalQ = message.originalQuestion || '';
                                            const retryPrompt = `${originalQ}\n\n(Use the table ${table} — the previous attempt returned 0 rows.)`;
                                            handleSmartQuery(retryPrompt);
                                          }}
                                          className="text-xs px-2.5 py-1 bg-white hover:bg-blue-100 active:bg-blue-200 border border-blue-300 text-blue-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                                          title="Click to re-run the question using this table"
                                        >
                                          ↻ {table}
                                        </button>
                                      ))}
                                    </div>
                                    <p className="text-[10px] text-blue-500 mt-1.5 italic">Tip: the AI will keep your original question and just swap the table.</p>
                                  </div>
                                )}
                                
                                {/* Suggestions */}
                                {message.suggestion?.alternatives && (
                                  <div className="text-xs text-gray-600">
                                    <p className="font-medium mb-1">Suggestions:</p>
                                    <ul className="list-disc list-inside space-y-0.5">
                                      {message.suggestion.alternatives.map((alt, idx) => (
                                        <li key={idx}>{alt}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Data Table - Prominently displayed when results exist.
                            When a Risk Report is rendered above, the entire data-table block
                            is wrapped in a `<details>` accordion (default collapsed) so the
                            report alone is the visual answer. Click the accordion header to
                            reveal the raw supporting evidence. */}
                        {message.results && message.results.length > 0 && (() => {
                          const _summary = message.analysis?.summary || '';
                          const _isRisk = /risk report|player risk|player profile.*kyc|scored areas/i.test(_summary);
                          const tableBlock = (
                            <div className={`overflow-hidden ${_isRisk ? 'pt-2' : 'mt-4 pt-3 border-t border-gray-100'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <button
                                  onClick={() => toggleTable(message.id)}
                                  className="text-xs text-[#D4AF37] hover:text-[#B8960F] flex items-center gap-1 transition-colors font-medium"
                                  data-testid={`toggle-table-${message.id}`}
                                >
                                  <Table2 className="w-3.5 h-3.5" />
                                  {expandedTables[message.id] ? 'Hide table' : 'View data table'}
                                  <span className="text-gray-400 font-normal ml-1">({message.rowCount || message.results.length} rows)</span>
                                  <ChevronRight className={`w-3 h-3 transition-transform ${expandedTables[message.id] ? 'rotate-90' : ''}`} />
                                </button>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => copyTableToClipboard(message.results, message.id)}
                                    className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#D4AF37] transition-colors px-2 py-1 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200"
                                    title="Copy table (paste into Excel)"
                                    data-testid={`copy-table-${message.id}`}
                                  >
                                    {copiedId === `table-${message.id}` ? (
                                      <><Check className="w-3 h-3 text-emerald-500" /> Copied</>
                                    ) : (
                                      <><ClipboardCopy className="w-3 h-3" /> Copy Table</>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleExportResults(message.id, message.results, message.content?.substring(0, 50) || 'Query')}
                                    className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-[#D4AF37] transition-colors px-2 py-1 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200"
                                    title="Download as Excel file"
                                    data-testid={`download-excel-${message.id}`}
                                  >
                                    <FileSpreadsheet className="w-3 h-3" /> Download Excel
                                  </button>
                                </div>
                              </div>

                              {expandedTables[message.id] && (
                              <div className="data-table-container overflow-x-auto border border-gray-200 rounded-lg text-xs" style={{ maxWidth: '100%', maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="w-full table-auto" data-testid={`data-table-${message.id}`}>
                                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                    <tr>
                                      <th className="px-2 py-1.5 text-left font-semibold text-gray-500 whitespace-nowrap w-8 text-[10px]">#</th>
                                      {Object.keys(message.results[0]).map((key) => (
                                        <th key={key} className="px-3 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap text-xs">
                                          {key}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {message.results.slice(0, 50).map((row, idx) => (
                                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        <td className="px-2 py-1 text-gray-400 text-[10px]">{idx + 1}</td>
                                        {Object.values(row).map((val, i) => (
                                          <td key={i} className="px-3 py-1 text-gray-700 whitespace-nowrap max-w-[150px] truncate text-xs" title={val !== null ? String(val) : ''}>
                                            {formatCellValue(val)}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {message.results.length > 50 && (
                                  <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-200 text-center">
                                    Showing 50 of {message.results.length} rows. Download Excel for full data.
                                  </div>
                                )}
                              </div>
                            )}
                            </div>
                          );

                          if (_isRisk) {
                            return (
                              <details className="mt-4 pt-3 border-t border-gray-100 group" data-testid={`raw-data-accordion-${message.id}`}>
                                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 py-1 select-none">
                                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                                  <Table2 className="w-3.5 h-3.5" />
                                  <span className="font-medium">Show supporting raw query data</span>
                                  <span className="text-gray-400 ml-1">({message.rowCount || message.results.length} rows)</span>
                                </summary>
                                <div className="mt-2">
                                  {tableBlock}
                                </div>
                              </details>
                            );
                          }
                          return tableBlock;
                        })()}
                        
                        {/* Visualization Toggle & Chart */}
                        {message.visualization && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => toggleChart(message.id)}
                              className="text-xs text-[#D4AF37] hover:text-[#B8960F] flex items-center gap-1 transition-colors font-medium"
                              data-testid={`toggle-chart-${message.id}`}
                            >
                              <BarChart3 className="w-3 h-3" />
                              {expandedCharts[message.id] ? 'Hide visualization' : 'Show data visualization'}
                              <ChevronRight className={`w-3 h-3 transition-transform ${expandedCharts[message.id] ? 'rotate-90' : ''}`} />
                            </button>
                            
                            {expandedCharts[message.id] && (
                              <DataVisualization visualization={message.visualization} />
                            )}
                          </div>
                        )}
                        
                        {/* Detective-style Investigation Options - After data/visualization */}
                        {message.analysis?.investigation_options && message.analysis.investigation_options.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="p-3 bg-amber-50/50 border border-amber-200 rounded-lg">
                              <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                                <Search className="w-4 h-4" />
                                🔍 Investigate potential causes:
                              </p>
                              <div className="space-y-2">
                                {message.analysis.investigation_options.map((option, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      const investigateQuery = option.check_query;
                                      if (investigateQuery) {
                                        // Build a single user message with the investigation context.
                                        // handleSmartQuery/handleStagedQuery already append the user
                                        // message internally — calling setMessages here too caused a
                                        // duplicate bubble (one with the prefix, one with just the query).
                                        const wrappedQuery = `🔍 Investigating: ${option.cause}\n\n${investigateQuery}`;
                                        if (useSmartMode) {
                                          handleSmartQuery(wrappedQuery);
                                        } else {
                                          handleStagedQuery(wrappedQuery);
                                        }
                                      }
                                    }}
                                    disabled={loading}
                                    className="w-full text-left p-2 bg-white hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors group disabled:opacity-50"
                                    data-testid={`investigate-option-${idx}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="w-5 h-5 flex items-center justify-center bg-amber-200 text-amber-800 rounded-full text-xs font-bold group-hover:bg-amber-300">
                                        {option.id || idx + 1}
                                      </span>
                                      <span className="text-sm font-medium text-gray-800">{option.cause}</span>
                                      <ChevronRight className="w-4 h-4 text-amber-500 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <p className="ml-7 text-xs text-gray-500 mt-0.5 truncate">
                                      → {option.check_query}
                                    </p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Error with SQL - Show SQL prominently for debugging */}
                        {user?.role === 'admin' && message.error && message.sql && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                              <Code className="w-3 h-3" />
                              SQL Query That Failed:
                            </p>
                            <pre className="text-xs bg-gray-900 text-red-400 p-2 rounded overflow-x-auto max-h-48 font-mono">
                              {message.sql}
                            </pre>
                            {message.stage1_tables && (
                              <p className="text-xs text-red-600 mt-2">
                                Tables selected: {message.stage1_tables.join(', ')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Technical Details (SQL only) - ADMIN ONLY */}
                        {user?.role === 'admin' && message.sql && !message.error && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => toggleDetails(message.id)}
                              className="text-xs text-gray-500 hover:text-[#D4AF37] flex items-center gap-1 transition-colors"
                            >
                              <Code className="w-3 h-3" />
                              {expandedDetails[message.id] ? 'Hide SQL' : 'Show SQL query'}
                              <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1">Admin</Badge>
                              <ChevronRight className={`w-3 h-3 transition-transform ${expandedDetails[message.id] ? 'rotate-90' : ''}`} />
                            </button>
                            
                            {expandedDetails[message.id] && (
                              <div className="mt-2">
                                {/* Table Explanations (Why tables were selected) */}
                                {message.tableExplanations && message.tableExplanations.length > 0 && (
                                  <div className="mb-3 p-2 bg-purple-50 border border-purple-100 rounded">
                                    <p className="text-xs font-medium text-purple-700 mb-1">Why These Tables?</p>
                                    <ul className="space-y-1">
                                      {message.tableExplanations.map((exp, idx) => (
                                        <li key={idx} className="text-xs text-gray-600">
                                          <span className="font-medium text-purple-600">{exp.table?.split('.').pop() || exp.table}</span>: {exp.why}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {message.server && message.database && (
                                  <div className="flex items-center gap-2 text-xs mb-2 text-gray-600">
                                    <Database className="w-3 h-3" />
                                    <span>{message.server}.{message.database}</span>
                                    {message.rowCount !== undefined && (
                                      <span className="text-emerald-600">• {message.rowCount} rows</span>
                                    )}
                                  </div>
                                )}
                                <div className="relative overflow-hidden">
                                  <button
                                    onClick={() => copyToClipboard(message.sql, `sql-${message.id}`)}
                                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-200 p-1 z-10"
                                  >
                                    {copiedId === `sql-${message.id}` ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                                  </button>
                                  <pre className="sql-block bg-gray-900 text-gray-100 p-3 pr-8 rounded text-xs font-mono whitespace-pre-wrap break-all overflow-x-auto max-w-full">
                                    {message.sql}
                                  </pre>
                                </div>
                                
                                {/* Optimization Hints */}
                                {message.optimizationHints && message.optimizationHints.length > 0 && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded">
                                    <p className="text-xs font-medium text-blue-700 mb-1">💡 Optimization Tips</p>
                                    <ul className="space-y-0.5">
                                      {message.optimizationHints.map((hint, idx) => (
                                        <li key={idx} className="text-xs text-gray-600">• {hint}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {/* EXPLAIN Analysis */}
                                {message.explainAnalysis && message.explainAnalysis.success && (
                                  <div className={`mt-2 p-2 border rounded ${
                                    message.explainAnalysis.analysis?.risk_level === 'high' 
                                      ? 'bg-red-50 border-red-200' 
                                      : message.explainAnalysis.analysis?.risk_level === 'medium'
                                        ? 'bg-amber-50 border-amber-200'
                                        : 'bg-green-50 border-green-200'
                                  }`}>
                                    <p className={`text-xs font-medium mb-2 flex items-center gap-1 ${
                                      message.explainAnalysis.analysis?.risk_level === 'high'
                                        ? 'text-red-700'
                                        : message.explainAnalysis.analysis?.risk_level === 'medium'
                                          ? 'text-amber-700'
                                          : 'text-green-700'
                                    }`}>
                                      {message.explainAnalysis.analysis?.risk_level === 'high' ? '⚠️ High Risk Query' :
                                       message.explainAnalysis.analysis?.risk_level === 'medium' ? '⚡ Medium Complexity' : '✅ Optimized Query'}
                                      <span className="font-normal ml-2">
                                        (~{(message.explainAnalysis.analysis?.estimated_rows || 0).toLocaleString()} rows estimated)
                                      </span>
                                    </p>
                                    
                                    {/* Warnings */}
                                    {message.explainAnalysis.warnings && message.explainAnalysis.warnings.length > 0 && (
                                      <ul className="space-y-0.5 mb-2">
                                        {message.explainAnalysis.warnings.slice(0, 3).map((warn, idx) => (
                                          <li key={idx} className="text-xs text-gray-600">{warn}</li>
                                        ))}
                                      </ul>
                                    )}
                                    
                                    {/* Indexes Used */}
                                    {message.explainAnalysis.analysis?.indexes_used?.length > 0 && (
                                      <p className="text-xs text-gray-600">
                                        <span className="font-medium">Indexes used:</span>{' '}
                                        {message.explainAnalysis.analysis.indexes_used.map(i => `${i.table}.${i.index}`).join(', ')}
                                      </p>
                                    )}
                                    
                                    {/* Recommendations */}
                                    {message.explainAnalysis.recommendations && message.explainAnalysis.recommendations.length > 0 && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <p className="text-xs font-medium text-gray-700 mb-1">Recommendations:</p>
                                        {message.explainAnalysis.recommendations.slice(0, 2).map((rec, idx) => (
                                          <div key={idx} className="text-xs text-gray-600 mb-1">
                                            <span className={`font-medium ${rec.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`}>
                                              {rec.severity === 'high' ? '🔴' : '🟡'}
                                            </span>{' '}
                                            {rec.suggestion}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Follow-up Questions - Clickable Buttons */}
                        {message.followUpQuestions && message.followUpQuestions.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <Lightbulb className="w-3 h-3 text-amber-500" />
                              What would you like to explore next?
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {message.followUpQuestions.map((question, idx) => (
                                <Button
                                  key={idx}
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setQuery(question);
                                    handleSmartQuery(question);
                                  }}
                                  className="text-left h-auto py-2 px-3 text-xs hover:bg-blue-50 hover:border-blue-300 transition-colors"
                                  data-testid={`follow-up-${message.id}-${idx}`}
                                >
                                  <span className="text-gray-700">{question}</span>
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Footer */}
                        <div className="mt-3 pt-2 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {new Date(message.timestamp).toLocaleTimeString()}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setFeedbackOpen(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors border ${
                                  feedbackOpen[message.id] 
                                    ? 'text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/5' 
                                    : 'text-gray-400 border-transparent hover:text-[#D4AF37] hover:border-gray-200 hover:bg-gray-50'
                                }`}
                                title="Correct this response — teach the AI"
                                data-testid={`teach-ai-${message.id}`}
                              >
                                <GraduationCap className="w-3 h-3" />
                                Teach AI
                              </button>
                              <button
                                onClick={() => handleOpenAnnotation(message.id)}
                                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors border text-gray-400 border-transparent hover:text-[#D4AF37] hover:border-gray-200 hover:bg-gray-50"
                                title="Add team annotation"
                                data-testid={`annotate-${message.id}`}
                              >
                                <StickyNote className="w-3 h-3" />
                                Notes {annotations[message.id]?.length > 0 && `(${annotations[message.id].length})`}
                              </button>
                              <button
                                onClick={() => handleCompareQuery(message.originalQuery)}
                                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors border text-gray-400 border-transparent hover:text-[#D4AF37] hover:border-gray-200 hover:bg-gray-50"
                                title="Compare time periods"
                                data-testid={`compare-${message.id}`}
                              >
                                <GitCompare className="w-3 h-3" />
                                Compare
                              </button>
                              <button
                                onClick={() => handleSaveAsTemplate(message.originalQuery)}
                                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors border text-gray-400 border-transparent hover:text-amber-500 hover:border-gray-200 hover:bg-gray-50"
                                title="Save as template"
                                data-testid={`save-template-${message.id}`}
                              >
                                <Star className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => copyToClipboard(message.content, message.id)}
                                className="text-gray-400 hover:text-gray-700 transition-colors p-1"
                                title="Copy analysis text"
                                data-testid={`copy-response-${message.id}`}
                              >
                                {copiedId === message.id ? (
                                  <Check className="w-4 h-4 text-emerald-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>
                          
                          {/* Inline Feedback Form */}
                          {feedbackOpen[message.id] && (
                            <div className="mt-2 p-2.5 bg-amber-50/50 border border-amber-200/60 rounded-lg" data-testid={`feedback-form-${message.id}`}>
                              <p className="text-[10px] text-amber-700 mb-1.5 font-medium">
                                Tell the AI what it got wrong. This will be remembered for all future queries.
                              </p>
                              <textarea
                                value={feedbackText[message.id] || ''}
                                onChange={(e) => setFeedbackText(prev => ({ ...prev, [message.id]: e.target.value }))}
                                placeholder="e.g., Don't use DATE(created) = CURDATE(), use created >= CURDATE() instead to preserve index usage"
                                className="w-full text-xs border border-amber-200 rounded px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37] resize-none"
                                rows={2}
                                data-testid={`feedback-input-${message.id}`}
                              />
                              <div className="flex items-center justify-end gap-2 mt-1.5">
                                <button
                                  onClick={() => {
                                    setFeedbackOpen(prev => ({ ...prev, [message.id]: false }));
                                    setFeedbackText(prev => ({ ...prev, [message.id]: '' }));
                                  }}
                                  className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-0.5"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSubmitFeedback(
                                    message.id, 
                                    message.originalQuestion || message.question || message.content || '', 
                                    message.sql || ''
                                  )}
                                  disabled={!feedbackText[message.id]?.trim() || feedbackSending}
                                  className="text-[10px] bg-[#D4AF37] text-white px-3 py-1 rounded font-medium hover:bg-[#B8960F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  data-testid={`feedback-submit-${message.id}`}
                                >
                                  {feedbackSending ? 'Saving...' : 'Save Correction'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {message.type === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              
              {(loading || estimating) && (
                <div className="flex gap-4 justify-start animate-slide-up">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#D4AF37] animate-pulse" />
                  </div>
                  <div className="chat-bubble-ai p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                      <span className="text-gray-700 text-sm font-medium">
                        {estimating ? 'Estimating query complexity...' : (thinkingStatus || 'Processing...')}
                      </span>
                      {thinkingStartTime && !estimating && (
                        <span className="text-gray-400 text-xs ml-auto">
                          {((Date.now() - thinkingStartTime) / 1000).toFixed(0)}s
                        </span>
                      )}
                    </div>
                    
                    {/* Step-by-step progress like Emergent */}
                    {!estimating && thinkingSteps.length > 0 && (
                      <div className="border-l-2 border-[#D4AF37]/30 pl-3 space-y-1 mt-3">
                        {/* Slow-query hint — surfaces after 30s of continuous waiting */}
                        {slowQueryHint && (
                          <div
                            data-testid="slow-query-hint"
                            className="mb-2 -ml-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-md flex items-start gap-2.5 animate-fade-in"
                          >
                            <span className="text-base leading-none mt-0.5">⏳</span>
                            <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                              <div className="font-medium mb-0.5">AI is taking longer than usual.</div>
                              <div className="text-amber-800/90">
                                This can happen for complex multi-server queries, large date ranges, or wide brand lists.
                                If this isn't what you intended, click <span className="font-medium text-red-600">Stop</span> below and rephrase — that's often faster than waiting.
                              </div>
                            </div>
                          </div>
                        )}
                        {thinkingSteps.map((step, idx) => {
                          // Only enable expansion if `detail` contains substantive content beyond `elapsed`
                          const detailKeys = step.detail ? Object.keys(step.detail).filter(k => k !== 'elapsed' && step.detail[k] != null && step.detail[k] !== '') : [];
                          const hasExpandableDetail = detailKeys.length > 0;
                          return (
                          <div key={idx} className="animate-fade-in">
                            <div 
                              className={`flex items-center gap-2 text-xs py-1.5 px-2 rounded transition-colors ${
                                hasExpandableDetail ? 'cursor-pointer hover:bg-[#D4AF37]/5' : ''
                              }`}
                              onClick={() => hasExpandableDetail && setExpandedDetails(prev => ({
                                ...prev,
                                [`thinking-${idx}`]: !prev[`thinking-${idx}`]
                              }))}
                            >
                              <span className="w-5 text-center flex-shrink-0">{step.icon}</span>
                              <span className={`flex-1 ${
                                step.status === 'running' ? 'text-gray-700 font-medium' : 
                                step.status === 'done' ? 'text-gray-500' : 
                                step.status === 'waiting' ? 'text-amber-600' : 'text-gray-400'
                              }`}>
                                {step.text}
                              </span>
                              
                              {/* Elapsed time badge */}
                              {step.detail?.elapsed && (
                                <span className="text-[10px] text-gray-400 font-mono">
                                  {step.detail.elapsed}
                                </span>
                              )}
                              
                              {/* Status indicator */}
                              {step.status === 'done' && (
                                <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                              )}
                              {step.status === 'running' && (
                                <Loader2 className="w-3.5 h-3.5 text-[#D4AF37] animate-spin flex-shrink-0" />
                              )}
                              {step.status === 'waiting' && (
                                <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              )}
                              
                              {/* Expand chevron only when there's substantive detail content */}
                              {hasExpandableDetail && (
                                <ChevronRight className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${
                                  expandedDetails[`thinking-${idx}`] ? 'rotate-90' : ''
                                }`} />
                              )}
                            </div>
                            
                            {/* Expandable detail content */}
                            {hasExpandableDetail && expandedDetails[`thinking-${idx}`] && (
                              <div className="ml-7 mt-1 mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600 border-l-2 border-[#D4AF37]/20">
                                {step.detail.reasoning && (
                                  <p className="mb-1"><span className="font-medium">Reasoning:</span> {step.detail.reasoning}</p>
                                )}
                                {step.detail.tables && (
                                  <div className="mb-1">
                                    <span className="font-medium">Tables:</span>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {step.detail.tables.map((t, i) => (
                                        <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
                                          {t.split('.').pop()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* SQL in thinking steps - ADMIN ONLY */}
                                {user?.role === 'admin' && step.detail.sql && (
                                  <div className="mb-1">
                                    <span className="font-medium">SQL:</span>
                                    <pre className="mt-0.5 p-1.5 bg-gray-900 text-green-400 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                                      {step.detail.sql}
                                    </pre>
                                  </div>
                                )}
                                {step.detail.understanding && (
                                  <p className="mb-1"><span className="font-medium">Understanding:</span> {step.detail.understanding}</p>
                                )}
                                {step.detail.plan && (
                                  <p className="mb-1"><span className="font-medium">Plan:</span> {step.detail.plan}</p>
                                )}
                                {step.detail.server && (
                                  <p className="mb-1"><span className="font-medium">Target:</span> {step.detail.server}.{step.detail.database}</p>
                                )}
                                {step.detail.rowCount !== undefined && (
                                  <p className="mb-1"><span className="font-medium">Rows:</span> {step.detail.rowCount}</p>
                                )}
                                {step.detail.contextSize && (
                                  <p className="text-gray-400"><span className="font-medium">Context:</span> {step.detail.contextSize.toLocaleString()} chars</p>
                                )}
                                {step.detail.confidence && (
                                  <p className="mb-1"><span className="font-medium">Confidence:</span> {step.detail.confidence}</p>
                                )}
                                {step.detail.summary && (
                                  <p className="mb-1"><span className="font-medium">Summary:</span> {step.detail.summary}</p>
                                )}
                                {step.detail.corrections && (
                                  <p className="mb-1"><span className="font-medium">Corrections:</span> {step.detail.corrections.join(', ')}</p>
                                )}
                                {step.detail.interpretedQuestion && (
                                  <p className="mb-1"><span className="font-medium">Interpreted as:</span> "{step.detail.interpretedQuestion}"</p>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Fallback simple status */}
                    {!estimating && thinkingSteps.length === 0 && (
                      <div className="text-xs text-gray-400 space-y-1 mt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                          <span>Connected to database</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse"></div>
                          <span>AI is working...</span>
                        </div>
                      </div>
                    )}
                    
                    {/* STOP Button */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleStopQuery}
                        className="text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300 w-full"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Stop - AI is going wrong direction
                      </Button>
                      <p className="text-[10px] text-gray-400 mt-2 text-center">
                        Click to cancel and explain what you actually need
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Context Limit Warning Banner */}
        {(isApproachingLimit || isAtLimit) && messages.length > 0 && (
          <div className={`mx-4 mt-2 p-3 rounded-lg border flex items-center justify-between gap-3 ${
            isAtLimit 
              ? 'bg-amber-50 border-amber-300 text-amber-800' 
              : 'bg-blue-50 border-blue-200 text-blue-700'
          }`} data-testid="context-warning-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`w-4 h-4 ${isAtLimit ? 'text-amber-500' : 'text-blue-500'}`} />
              <span className="text-sm">
                {isAtLimit 
                  ? `Context limit reached (${messageCount}/${CONTEXT_LIMIT} messages). Summarize to continue effectively.`
                  : `Approaching context limit (${messageCount}/${CONTEXT_LIMIT} messages).`
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSummarizeContext}
                disabled={summarizing}
                className={`text-xs ${isAtLimit ? 'border-amber-400 hover:bg-amber-100' : 'border-blue-300 hover:bg-blue-100'}`}
                data-testid="summarize-context-btn"
              >
                {summarizing ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Summarizing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Summarize & Continue
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleStartNewConversation}
                className="text-xs text-gray-500 hover:text-gray-700"
                data-testid="new-conversation-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                New Chat
              </Button>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-gray-100">
          {/* Compact Control Bar */}
          <div className="flex items-center justify-between mb-3 gap-2">
            {/* Left: AI Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => { setUseSmartMode(true); setUseAutonomousMode(false); }}
                className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                  useSmartMode 
                    ? 'bg-white text-emerald-700 font-medium shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Smart AI: Two-stage focused query"
              >
                Smart AI
              </button>
              <button
                type="button"
                onClick={() => { setUseSmartMode(false); setUseAutonomousMode(false); }}
                className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                  !useSmartMode && !useAutonomousMode 
                    ? 'bg-white text-amber-700 font-medium shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Staged: Step-by-step with approval"
              >
                Staged
              </button>
              <button
                type="button"
                onClick={() => { setUseSmartMode(false); setUseAutonomousMode(true); }}
                className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                  useAutonomousMode 
                    ? 'bg-white text-blue-700 font-medium shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Full Context: 530k context (may lose focus)"
              >
                Full
              </button>
              
              {/* Admin Training Mode */}
              {user?.role === 'admin' && useSmartMode && (
                <button
                  type="button"
                  onClick={() => setAdminPreviewMode(!adminPreviewMode)}
                  className={`px-3 py-1.5 text-xs rounded-md transition-all ml-1 flex items-center gap-1.5 ${
                    adminPreviewMode 
                      ? 'bg-purple-500 text-white font-medium shadow-sm' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                  }`}
                  title="Admin Training Mode: Review SQL + EXPLAIN before execution. Provide feedback to train AI."
                >
                  <Edit className="w-3.5 h-3.5" />
                  {adminPreviewMode ? 'Training ON' : 'Training'}
                </button>
              )}
              
              {/* Sync Schema Button - Admin Only */}
              {user?.role === 'admin' && (
                <button
                  type="button"
                  onClick={async () => {
                    if (syncingSchema) return;
                    setSyncingSchema(true);
                    try {
                      toast.info('Syncing database schemas...');
                      const result = await refreshAllSchemas();
                      if (result.success) {
                        const refreshedCount = result.refreshed?.length || 0;
                        const totalTables = result.refreshed?.reduce((sum, r) => sum + (r.tables || 0), 0) || 0;
                        toast.success(
                          <div>
                            <strong>Schema Synced!</strong>
                            <br />
                            <span className="text-sm">{refreshedCount} servers, {totalTables} tables refreshed</span>
                          </div>
                        );
                      } else {
                        toast.error(result.message || 'Sync failed');
                      }
                    } catch (error) {
                      toast.error('Failed to sync schemas: ' + (error.message || 'Unknown error'));
                    } finally {
                      setSyncingSchema(false);
                    }
                  }}
                  disabled={syncingSchema}
                  className={`px-3 py-1.5 text-xs rounded-md transition-all ml-1 flex items-center gap-1.5 ${
                    syncingSchema
                      ? 'bg-blue-200 text-blue-600 cursor-not-allowed'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800'
                  }`}
                  title="Refresh all database schemas so AI has the latest table structures and column names"
                  data-testid="sync-schema-btn"
                >
                  {syncingSchema ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  {syncingSchema ? 'Syncing...' : 'Sync Schema'}
                </button>
              )}
            </div>
            
            {/* Center: Analyst Role Dropdown (only for Smart AI) */}
            {useSmartMode && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedAnalystRole}
                  onChange={(e) => canSwitchRoles && setSelectedAnalystRole(e.target.value)}
                  disabled={!canSwitchRoles}
                  className="text-xs bg-white border border-gray-200 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  title="Select analyst perspective"
                  data-testid="analyst-role-select"
                >
                  {availableRoles.map((roleId) => {
                    const role = analystRoles[roleId];
                    if (!role) return null;
                    return (
                      <option key={roleId} value={roleId}>
                        {role.icon} {role.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            
            {/* Right: Quick Actions (icon buttons) */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowTemplates(true)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Query Templates"
                data-testid="templates-btn"
              >
                <Bookmark className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleSaveAsTemplate(query)}
                disabled={!query.trim()}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40"
                title="Save as Template"
                data-testid="save-template-btn"
              >
                <Star className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleExportConversation('markdown')}
                disabled={messages.length === 0}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40"
                title="Export Conversation"
                data-testid="export-btn"
              >
                <FileText className="w-4 h-4" />
              </button>
              {speechSupported && (
                <button
                  type="button"
                  onClick={toggleVoiceInput}
                  className={`p-1.5 rounded-md transition-colors ${
                    isListening 
                      ? 'bg-red-500 text-white' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title={isListening ? "Stop Listening" : "Voice Input"}
                  data-testid="voice-btn"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? "🎤 Listening... speak your query" : "Ask about your data... (Shift+Enter to send, Enter for new line)"}
                className={`min-h-[52px] max-h-32 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 resize-none pr-12 focus:border-[#D4AF37] focus:bg-white ${isListening ? 'border-red-400 bg-red-50' : ''}`}
                disabled={loading || estimating}
                data-testid="query-input"
              />
              {/* AI provider health pill — sits in the bottom-right of the textarea */}
              <button
                type="button"
                onClick={checkLlmHealth}
                disabled={llmHealthLoading}
                className={`absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors flex items-center gap-1 ${
                  llmHealth === null
                    ? 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                    : llmHealth.ok
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-300'
                    : 'bg-red-50 border-red-200 text-red-700 hover:border-red-300'
                }`}
                title={
                  llmHealth === null
                    ? 'Click to ping the AI provider (Anthropic) and confirm uptime'
                    : llmHealth.ok
                    ? `Anthropic OK · ${llmHealth.latency_ms}ms`
                    : `Anthropic ${llmHealth.error_kind || 'error'}: ${llmHealth.error?.substring(0, 80) || ''}`
                }
                data-testid="check-ai-health-btn"
              >
                {llmHealthLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : llmHealth === null ? (
                  <>⚡ Check AI</>
                ) : llmHealth.ok ? (
                  <>✓ AI OK ({llmHealth.latency_ms}ms)</>
                ) : (
                  <>✗ AI DOWN</>
                )}
              </button>
            </div>
            <Button
              type="submit"
              disabled={loading || estimating || !query.trim()}
              className="btn-gold h-[52px] px-6"
              data-testid="send-query-btn"
            >
              {loading || estimating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>
        </div>
      </Card>
      </div>
      {/* End of Main Content */}

      {/* Query Estimate Confirmation Dialog */}
      <Dialog open={showEstimateDialog} onOpenChange={setShowEstimateDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="estimate-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              Query Estimate
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Review the estimated complexity before running
            </DialogDescription>
          </DialogHeader>
          
          {currentEstimate && (
            <div className="space-y-4 py-4">
              {/* Query preview */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 line-clamp-2">{pendingQuery}</p>
              </div>
              
              {/* Complexity badge */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Complexity:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getComplexityColor(currentEstimate.complexity)}`}>
                  {currentEstimate.complexity?.toUpperCase() || 'UNKNOWN'}
                </span>
              </div>
              
              {/* Time estimate */}
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">
                  Estimated time: <strong>{currentEstimate.estimated_time_display || 'Unknown'}</strong>
                </span>
              </div>
              
              {/* Data description */}
              {currentEstimate.data_description && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-800">{currentEstimate.data_description}</p>
                </div>
              )}
              
              {/* Warning if present */}
              {currentEstimate.warning && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">{currentEstimate.warning}</p>
                </div>
              )}
              
              {/* Servers involved */}
              {currentEstimate.servers_involved && currentEstimate.servers_involved.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Database className="w-4 h-4" />
                  <span>Servers: {currentEstimate.servers_involved.join(', ')}</span>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowEstimateDialog(false)}
              className="border-gray-200"
            >
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={handleRunInBackground}
              className="border-gray-200"
              data-testid="run-background-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Run in Background
            </Button>
            <Button 
              onClick={handleConfirmQuery}
              className="btn-gold"
              data-testid="confirm-query-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Run Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Background Queries Panel */}
      {showBackgroundPanel && backgroundQueries.length > 0 && (
        <div className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-testid="background-panel">
          <div className="flex items-center justify-between p-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-medium text-sm text-gray-900">Background Queries</span>
              <Badge variant="secondary" className="text-xs">
                {backgroundQueries.filter(q => q.status === 'running').length} running
              </Badge>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowBackgroundPanel(false)}
              className="h-6 w-6 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="max-h-60">
            <div className="divide-y divide-gray-100">
              {backgroundQueries.slice(0, 5).map(query => (
                <div key={query.query_id} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 truncate">{query.query_text}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {query.status === 'running' ? (
                          <span className="flex items-center gap-1 text-xs text-amber-600">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Running...
                          </span>
                        ) : query.status === 'completed' ? (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Completed
                          </span>
                        ) : query.status === 'cancelled' ? (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <X className="w-3 h-3" />
                            Cancelled
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-red-600">
                            <XCircle className="w-3 h-3" />
                            Failed
                          </span>
                        )}
                      </div>
                    </div>
                    {query.status === 'running' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelQuery(query.query_id)}
                        className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        title="Cancel query"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5 text-[#D4AF37]" />
              Query History
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] mt-4">
            {historyLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            ) : conversations.length > 0 ? (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleLoadConversation(conv)}
                    className="p-4 rounded-lg border border-gray-200 hover:border-[#D4AF37]/50 hover:bg-gray-50 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <MessageSquare className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {conv.firstQuery}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span>{conv.queries.length} message{conv.queries.length > 1 ? 's' : ''}</span>
                            {conv.connection_name && (
                              <>
                                <span>-</span>
                                <span>{conv.connection_name}</span>
                              </>
                            )}
                            <span>-</span>
                            <span>{new Date(conv.lastUpdated).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleDeleteConversation(conv.id, e)}
                          className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p>No query history yet</p>
                <p className="text-sm mt-1">Start asking questions to build your history</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-[#D4AF37]" />
              Query Templates
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Quickly access your saved queries
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px] pr-2">
            {templates.length > 0 ? (
              <div className="space-y-2">
                {/* Favorites first */}
                {templates.filter(t => t.is_favorite).length > 0 && (
                  <>
                    <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-500" /> Favorites
                    </p>
                    {templates.filter(t => t.is_favorite).map(template => (
                      <div
                        key={template.id}
                        onClick={() => handleUseTemplate(template)}
                        className="p-3 rounded-lg border border-gray-200 hover:border-[#D4AF37] hover:bg-amber-50 cursor-pointer transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">{template.name}</span>
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">{template.category}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.query}</p>
                            {template.description && (
                              <p className="text-xs text-gray-400 mt-1">{template.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={(e) => handleToggleFavorite(template.id, e)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            </button>
                            <button 
                              onClick={(e) => handleDeleteTemplate(template.id, e)}
                              className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Non-favorites */}
                {templates.filter(t => !t.is_favorite).length > 0 && (
                  <>
                    <p className="text-xs font-medium text-gray-500 mt-4 mb-2">All Templates</p>
                    {templates.filter(t => !t.is_favorite).map(template => (
                      <div
                        key={template.id}
                        onClick={() => handleUseTemplate(template)}
                        className="p-3 rounded-lg border border-gray-200 hover:border-[#D4AF37] hover:bg-amber-50 cursor-pointer transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">{template.name}</span>
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">{template.category}</span>
                              <span className="text-[10px] text-gray-400">Used {template.usage_count}x</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{template.query}</p>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={(e) => handleToggleFavorite(template.id, e)}
                              className="p-1 hover:bg-gray-100 rounded"
                            >
                              <Star className="w-4 h-4 text-gray-400 hover:text-amber-500" />
                            </button>
                            <button 
                              onClick={(e) => handleDeleteTemplate(template.id, e)}
                              className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Bookmark className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p>No templates saved yet</p>
                <p className="text-sm mt-1">Save your frequently used queries as templates</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              Save Query Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Template Name</label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Daily Active Users"
                className="border-gray-200"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select
                value={templateForm.category}
                onValueChange={(value) => setTemplateForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="metrics">Metrics</SelectItem>
                  <SelectItem value="users">Users</SelectItem>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="reports">Reports</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description (optional)</label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What does this query do?"
                rows={2}
                className="border-gray-200"
              />
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="text-xs font-medium text-gray-500">Query</label>
              <p className="text-sm text-gray-700 mt-1">{templateForm.query || query}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate} className="btn-gold">
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annotations Dialog */}
      <Dialog open={showAnnotationDialog} onOpenChange={setShowAnnotationDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-[#D4AF37]" />
              Annotations
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Add notes or corrections for your team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add new annotation */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Select
                  value={annotationType}
                  onValueChange={setAnnotationType}
                >
                  <SelectTrigger className="w-32 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">📝 Note</SelectItem>
                    <SelectItem value="correction">✏️ Correction</SelectItem>
                    <SelectItem value="insight">💡 Insight</SelectItem>
                    <SelectItem value="warning">⚠️ Warning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                value={annotationText}
                onChange={(e) => setAnnotationText(e.target.value)}
                placeholder="Add your annotation..."
                rows={2}
                className="border-gray-200"
              />
              <Button onClick={handleSaveAnnotation} size="sm" className="btn-gold">
                <Plus className="w-4 h-4 mr-1" /> Add Annotation
              </Button>
            </div>
            
            {/* Existing annotations */}
            {annotations[currentAnnotationQueryId]?.length > 0 && (
              <div className="space-y-2 border-t border-gray-200 pt-4">
                <p className="text-xs font-medium text-gray-500">Previous Annotations</p>
                {annotations[currentAnnotationQueryId].map(annotation => (
                  <div key={annotation.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs">
                            {annotation.annotation_type === 'note' && '📝'}
                            {annotation.annotation_type === 'correction' && '✏️'}
                            {annotation.annotation_type === 'insight' && '💡'}
                            {annotation.annotation_type === 'warning' && '⚠️'}
                          </span>
                          <span className="text-xs text-gray-500">{annotation.created_by_name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(annotation.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{annotation.text}</p>
                      </div>
                      <button 
                        onClick={() => handleDeleteAnnotation(annotation.id)}
                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Query Comparison Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-[#D4AF37]" />
              Compare Time Periods
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Run the same query for different time periods
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Query</label>
              <Textarea
                value={compareQuery}
                onChange={(e) => setCompareQuery(e.target.value)}
                placeholder="Enter your query..."
                rows={2}
                className="border-gray-200"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Period 1</label>
                <Select value={comparePeriod1} onValueChange={setComparePeriod1}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last 7 days">Last 7 Days</SelectItem>
                    <SelectItem value="last 30 days">Last 30 Days</SelectItem>
                    <SelectItem value="this month">This Month</SelectItem>
                    <SelectItem value="last month">Last Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Period 2</label>
                <Select value={comparePeriod2} onValueChange={setComparePeriod2}>
                  <SelectTrigger className="border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last 7 days">Last 7 Days</SelectItem>
                    <SelectItem value="last 30 days">Last 30 Days</SelectItem>
                    <SelectItem value="this month">This Month</SelectItem>
                    <SelectItem value="last month">Last Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>Cancel</Button>
            <Button onClick={runComparisonQuery} className="btn-gold">
              <GitCompare className="w-4 h-4 mr-2" />
              Run Comparison
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIQueryPage;
